/* eslint-disable react/prop-types */
"use client";
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import {
  normalizeMunicipalityId,
  readEventLocationFilter,
  writeEventLocationFilter,
} from "../../utils/eventLocationFilterStorage";
import {
  DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
  getEventDiscoveryLocationParams,
  normalizeOptionalEventDiscoveryRadiusKm,
} from "../../utils/eventDiscoveryRadius";
import { getEventCountryValue } from "../../components/events/eventGeoLabels";
import {
  EVENT_ATTENDANCE_OPENS_BEFORE_MS,
  useEventAttendanceBoundaryClock,
} from "../../hooks/useEventAttendanceWindow";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../../api/axios";
import CreateEventBanner from "../../components/events/CreateEventBanner";
import ConceptQuickComposer from "../../components/events/ConceptQuickComposer";
import CityLaunchBanner from "../../components/events/CityLaunchBanner";
import { CityLaunchContext } from "../../context/CityLaunchContext";
import NavigationTabs from "../../components/events/NavigationTabs";
import EventsFilters from "../../components/events/EventsFilters";
import EventList from "../../components/events/EventList";
import EventFeedSkeleton from "../../components/events/EventFeedSkeleton";
import EventCreateStep0 from "../../components/events/EventCreateStep0";
import EventDetails from "../../components/events/EventDetailsFullscreen";
import EventDescriptionModal from "../../components/events/EventDescriptionModal";
import SubscriptionModal from "../../components/events/SubscriptionModal";
import PhotoRequestModal from "../../components/events/PhotoRequestModal";
import SegmentedModeToggle from "../../components/common/SegmentedModeToggle";
import { DETAIL_PAGE_SWAP_VARIANTS } from "../../components/common/detailPageMotion";
import ParticipantRatingPage from "./ParticipantRatingPage";
import { useWaitlistUnread } from "../../context/WaitlistUnreadContext";
import { useParticipantRequestUnread } from "../../context/ParticipantRequestUnreadContext";
import { useHostFeedbackUnread } from "../../context/HostFeedbackUnreadContext";
import { useParticipantRatingUnread } from "../../context/ParticipantRatingUnreadContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { trackEventEssentialAnalytics } from "../../utils/essentialAnalytics";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { buildClubPath } from "../../utils/clubUrl";
import { ACCOUNT_REGISTER_ICON_SRC, buildAuthRequiredPath } from "../../utils/authRedirect";
import { acquireBodyClass } from "../../utils/bodyClassRefCount";
import { normalizeProfileCountry } from "../../constants/profileLocations";
import { buildEventMapState } from "../../utils/eventMapFocus";
import { normalizeSelectedEventCity } from "../../utils/eventCitySelection";
import { readOrganizerBannerMode, writeOrganizerBannerMode } from "../../utils/organizerBannerModeStorage";
import "../../styles/EventsHeaderResponsive.css";

// ────────── Constants ────────── //
const PAGE_SIZE = 20;
const ROBOTS_INDEX_NOIMAGE = "index,follow,noimageindex";
const GUEST_CATALOG_OPTIONS = [
  { value: "events", label: "Events" },
  { value: "clubs", label: "Clubs" },
];
const PUBLIC_CATALOG_NAV_DELAY_MS = 220;
const EVENTS_PAGE_BACKGROUND_STYLE = AUTH_GRADIENT_STYLE;
const DEFAULT_EVENT_COUNTRY = "Slovakia";
const PAGE_SWAP_VARIANTS = DETAIL_PAGE_SWAP_VARIANTS;
const EVENT_FEED_DESKTOP_SCROLL_CLASS = "lg:mx-0 lg:w-full lg:max-w-none lg:px-0";
const EVENT_FEED_SCROLLBAR_CLASS = "events-scrollbar-hidden";
const CATEGORY_EDGE_FADE_PX = 24;
const EVENT_FEED_DESKTOP_CONTENT_CLASS = "lg:mx-auto lg:w-[min(var(--vg-desktop-feed-content-max),calc(100%_-_96px))]";
const EVENT_DETAIL_DESKTOP_VIEW_CLASS = `-mx-[var(--vg-page-gutter)] flex-1 overflow-hidden pb-3 pt-[calc(104px+var(--vg-safe-area-top))] lg:pt-[104px] ${EVENT_FEED_DESKTOP_SCROLL_CLASS}`;
const EVENT_DETAIL_DESKTOP_SHELL_CLASS = "mx-auto h-full min-h-0 w-full max-w-[760px] px-4";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
  }
  return fallback;
};

const normalizeEventId = (value) => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : trimmed;
  }
  return null;
};

const normalizeEventText = (value, fallback = "") => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
};

const normalizeEventPayload = (event, depth = 0) => {
  if (!isPlainObject(event)) return null;

  const id = normalizeEventId(event.id);
  if (id === null || id === undefined || id === "") return null;

  const promotedRank = Number(event.promoted_rank);
  const isPromotedActive =
    typeof event.is_promoted_active !== "undefined"
      ? normalizeBoolean(event.is_promoted_active)
      : normalizeBoolean(event.promoted || event.is_promoted);
  const childEvents =
    depth > 1 || !Array.isArray(event.child_events)
      ? []
      : event.child_events
          .map((childEvent) => normalizeEventPayload(childEvent, depth + 1))
          .filter(Boolean);

  return {
    ...event,
    id,
    name: normalizeEventText(event.name, `Event ${id}`).trim() || `Event ${id}`,
    type: normalizeEventText(event.type, "event").trim() || "event",
    status: normalizeEventText(event.status, "active").trim() || "active",
    date_time: normalizeEventText(event.date_time, ""),
    country: normalizeEventText(event.country, ""),
    city: normalizeEventText(event.city, ""),
    color: normalizeEventText(event.color, "#ffffff") || "#ffffff",
    social_preference_fit: isPlainObject(event.social_preference_fit)
      ? event.social_preference_fit
      : null,
    child_events: childEvents,
    is_validated: normalizeBoolean(event.is_validated),
    is_favourite: normalizeBoolean(event.is_favourite),
    is_verified: normalizeBoolean(event.is_verified),
    is_platform_event: normalizeBoolean(event.is_platform_event),
    is_discovery_spotlight: normalizeBoolean(event.is_discovery_spotlight),
    is_hostless_concept: normalizeBoolean(event.is_hostless_concept),
    is_participant: normalizeBoolean(event.is_participant),
    join_by_request: normalizeBoolean(event.join_by_request),
    is_promoted: isPromotedActive,
    is_promoted_active: isPromotedActive,
    promoted_rank: Number.isFinite(promotedRank)
      ? promotedRank
      : Number.MAX_SAFE_INTEGER,
  };
};

class EventFeedErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("[ListSearch] event feed render failed", error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleRetry() {
    this.setState({ hasError: false });
    this.props.onRecover?.();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="mx-auto mt-8 max-w-[360px] rounded-[24px] border border-white/25 bg-white/12 px-5 py-6 text-center shadow-[0_20px_45px_rgba(0,0,0,0.22)]">
        <p className="text-[20px] font-extrabold leading-[24px] text-white">
          {this.props.title}
        </p>
        <p className="mt-2 text-[14px] font-semibold leading-[20px] text-white/68">
          {this.props.body}
        </p>
        <button
          type="button"
          onClick={this.handleRetry}
          className="mt-5 inline-flex h-[44px] items-center justify-center rounded-full border border-white/35 bg-white/12 px-6 text-[14px] font-extrabold text-white shadow-[0_10px_24px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.18)] transition hover:bg-white/18 active:scale-[0.98]"
        >
          {this.props.actionLabel}
        </button>
      </div>
    );
  }
}

const getParticipantRatingHeaderTitle = (language = "en") => {
  const lang = String(language || "en").toLowerCase();
  if (lang.startsWith("ru")) return "Рейтинг участника";
  if (lang.startsWith("sk")) return "Rating účastníka";
  if (lang.startsWith("cs")) return "Hodnocení účastníka";
  return "Participant rating";
};

const getSoldTicketsHeaderTitle = (language = "en") => {
  const lang = String(language || "en").toLowerCase();
  if (lang.startsWith("ru")) return "Проданные билеты";
  if (lang.startsWith("sk")) return "Predané lístky";
  if (lang.startsWith("cs")) return "Prodané vstupenky";
  return "Sold Tickets";
};

const HeaderIconButton = ({
  iconSrc,
  label,
  onClick,
  badgeCount = 0,
  disabled = false,
}) => {
  const badgeLabel =
    Number(badgeCount || 0) > 99 ? "99+" : Number(badgeCount || 0) > 0 ? String(badgeCount) : "";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`vg-glass-control relative flex h-[50px] w-[51px] shrink-0 items-center justify-center rounded-[99px] shadow-[0_1px_0_rgba(0,0,0,0.05),0_4px_4px_rgba(0,0,0,0.05),0_10px_10px_rgba(0,0,0,0.1)] backdrop-blur-[10px] transition active:scale-[0.98] ${
        disabled ? "opacity-45" : ""
      }`}
    >
      <img src={iconSrc} alt="" className="h-6 w-6 object-contain" />
      {badgeLabel ? (
        <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-[5px] text-[11px] font-extrabold leading-none text-[#0062D9] shadow-[0_0_8px_rgba(255,255,255,0.42)]">
          {badgeLabel}
        </span>
      ) : null}
    </button>
  );
};

const HeaderCategoryPanel = ({
  selected = [],
  onChange,
  onAddSubscription,
  isSubscriptionActive = false,
  userSubscriptions = [],
}) => {
  const { t, i18n } = useTranslation("events");
  const isRu = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("ru");
  const scrollerRef = useRef(null);
  const [edgeMask, setEdgeMask] = useState(null);
  const updateOverflowCue = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const position = Math.min(Math.max(scroller.scrollLeft, 0), maxScroll);
    const leftFade = Math.min(CATEGORY_EDGE_FADE_PX, position);
    const rightFade = maxScroll - position > 8;
    const nextMask = leftFade && rightFade
      ? `linear-gradient(to right, transparent 0px, black ${leftFade}px, black calc(100% - ${CATEGORY_EDGE_FADE_PX}px), transparent 100%)`
      : leftFade
        ? `linear-gradient(to right, transparent 0px, black ${leftFade}px)`
        : rightFade
          ? `linear-gradient(to right, black calc(100% - ${CATEGORY_EDGE_FADE_PX}px), transparent 100%)`
          : null;
    setEdgeMask(nextMask);
  }, []);

  useEffect(() => {
    updateOverflowCue();
    window.addEventListener("resize", updateOverflowCue);
    return () => window.removeEventListener("resize", updateOverflowCue);
  }, [updateOverflowCue, i18n.language, i18n.resolvedLanguage]);

  const categories = [
    { label: t("event_create.category_grid.party"), value: "party", desktopClassName: "lg:w-[100px]" },
    { label: t("event_create.category_grid.sport"), value: "sport", desktopClassName: "lg:w-[99px]" },
    { label: t("event_create.category_grid.adventures", { defaultValue: "Adventures" }), value: "recreation", desktopClassName: "lg:w-[136px]" },
    { label: t("event_create.category_grid.educational", { defaultValue: "Educational" }), value: "education", desktopClassName: "lg:w-[137px]" },
    { label: t("event_create.category_grid.games", { defaultValue: "Games" }), value: "traveling", desktopClassName: "lg:w-[99px]" },
    { label: t("event_create.category_grid.tracker", { defaultValue: "Tracker" }), value: null, desktopClassName: "lg:w-[82px] lg:!px-[12px]" },
  ];

  return (
    <div
      ref={scrollerRef}
      onScroll={updateOverflowCue}
      className={`flex w-full items-center gap-[5px] overflow-x-auto overscroll-x-none whitespace-nowrap ${EVENT_FEED_SCROLLBAR_CLASS} lg:justify-center lg:gap-[8px]`}
      style={{
        overscrollBehaviorX: "none",
        WebkitOverflowScrolling: "touch",
        ...(edgeMask ? { maskImage: edgeMask, WebkitMaskImage: edgeMask } : {}),
      }}
    >
      {categories.map((category) => {
        const isSubscriptionButton = category.value === null;
        const active = isSubscriptionButton
          ? isSubscriptionActive
          : selected.includes(category.value);
        const toneClass = isSubscriptionButton
          ? active
            ? "border border-transparent bg-white/25 text-white shadow-[0_0_12px_rgba(255,255,255,0.14)]"
            : "border border-transparent bg-white/20 text-white"
          : active
            ? "bg-white text-[#3E6FFB] shadow-[0_0_15px_rgba(255,255,255,0.25)]"
            : "border border-white/20 bg-transparent text-white";

        return (
          <button
            key={category.value || "subscription"}
            type="button"
            className={`h-[35px] w-auto shrink-0 whitespace-nowrap rounded-[50px] px-[14px] pb-[7px] pt-[6px] font-bold leading-[22px] transition active:scale-[0.98] lg:px-[19px] lg:text-[13px] ${isRu ? "text-[12px]" : "text-[13px]"} ${category.desktopClassName} ${toneClass}`}
            onClick={() => {
              if (isSubscriptionButton) {
                onAddSubscription?.(userSubscriptions);
                return;
              }
              if (selected.includes(category.value)) {
                onChange?.(selected.filter((value) => value !== category.value));
              } else {
                onChange?.([...selected, category.value]);
              }
            }}
          >
            <span className="block">{category.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const EventsModeHeader = ({
  mode,
  isGuest = false,
  isDetailView = false,
  detailTitle = "My Rating",
  catalogValue = "events",
  feedSection = "events",
  hostSection = "panel",
  onCatalogChange,
  onFeedSectionChange,
  onHostSectionChange,
  onBackToEvents,
  onPrimaryActionClick,
  onMapClick,
  participantRatingUnread,
  hostAttentionCount = 0,
  ticketsAttentionCount = 0,
  mapDisabled = false,
  categoriesOpen = false,
  onToggleCategories,
  selectedCategories,
  onCategoriesChange,
  onAddSubscription,
  isSubscriptionActive = false,
  userSubscriptions = [],
  hideCategoryToggle = false,
}) => {
  const { t, i18n } = useTranslation("events");
  const reduceMotion = typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const categoryHeaderMountedRef = useRef(false);
  useEffect(() => {
    categoryHeaderMountedRef.current = true;
  }, []);
  const isSlovak = /^(sk|cs)/.test(
    String(i18n.resolvedLanguage || i18n.language || "").toLowerCase()
  );
  const memberToggleLayout = isSlovak
    ? {
        className: "w-[236px] vg-events-mode-toggle--balanced",
      }
    : {
        className: "w-[199px] vg-events-mode-toggle--asymmetric",
      };
  const isHostMode = mode === "host";
  const showCategoryControls = !isHostMode && !isDetailView && !hideCategoryToggle;
  const categorySwipeRef = useRef({ handled: false, pointerId: null, x: 0, y: 0 });
  const primaryIconSrc = isGuest
    ? ACCOUNT_REGISTER_ICON_SRC
    : isHostMode
    ? "/assets/events/payable_icon.svg"
    : "/assets/events/participant_rating_icon.svg";
  const primaryLabel = isGuest
    ? "Create account"
    : isDetailView
    ? "Back to events"
    : isHostMode
    ? "My tickets"
    : "Participant rating";
  const primaryBadgeCount = isGuest || isHostMode || isDetailView ? 0 : participantRatingUnread;
  const toggleValue = isGuest ? catalogValue : isHostMode ? hostSection : feedSection;
  const toggleOptions = isGuest
    ? GUEST_CATALOG_OPTIONS
    : isHostMode
      ? [
          { value: "panel", label: t("catalog_sections.panel") },
          { value: "tickets", label: t("catalog_sections.tickets") },
        ]
      : [
          { value: "events", label: t("catalog_sections.events") },
          { value: "concepts", label: t("catalog_sections.concepts") },
        ];
  const firstToggleValue = isHostMode ? "panel" : "events";
  const secondToggleValue = isGuest ? "clubs" : isHostMode ? "tickets" : "concepts";
  const handleCategorySwipeStart = useCallback((event) => {
    if (!showCategoryControls) return;
    categorySwipeRef.current = {
      handled: false,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }, [showCategoryControls]);
  const handleCategorySwipeEnd = useCallback((event) => {
    const start = categorySwipeRef.current;
    if (!showCategoryControls || start.pointerId !== event.pointerId) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    const isVerticalSwipe = Math.abs(dy) >= 18 && Math.abs(dy) > Math.abs(dx) * 1.2;

    if (isVerticalSwipe) {
      categorySwipeRef.current = { ...start, handled: true };
      if (dy > 0 && !categoriesOpen) {
        onToggleCategories?.();
      }
      return;
    }

    categorySwipeRef.current = { handled: false, pointerId: null, x: 0, y: 0 };
  }, [categoriesOpen, onToggleCategories, showCategoryControls]);
  const handleCategorySwipeCancel = useCallback(() => {
    categorySwipeRef.current = { handled: false, pointerId: null, x: 0, y: 0 };
  }, []);
  const handleCategoryToggleClick = useCallback(() => {
    if (categorySwipeRef.current.handled) {
      categorySwipeRef.current = { handled: false, pointerId: null, x: 0, y: 0 };
      return;
    }
    onToggleCategories?.();
  }, [onToggleCategories]);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-40 h-[calc(126px+var(--vg-safe-area-top))] overflow-visible px-[var(--vg-page-gutter)] lg:left-1/2 lg:right-auto lg:h-[126px] lg:w-[var(--vg-desktop-top-panel-width)] lg:-translate-x-1/2 lg:px-0"
    >
      <div
        className={`absolute inset-x-0 top-0 rounded-b-[45px] bg-gradient-to-r from-[rgba(13,24,55,0.58)] to-[rgba(37,69,157,0.58)] shadow-[0_20px_34px_rgba(0,0,0,0.18)] backdrop-blur-[34px] transition-[height] duration-200 motion-reduce:transition-none lg:rounded-b-[var(--vg-desktop-top-panel-radius)] lg:rounded-t-none ${
          isDetailView
            ? "h-[calc(92px+var(--vg-safe-area-top))] pointer-events-none lg:h-[92px]"
            : showCategoryControls && categoriesOpen
            ? "h-[calc(126px+var(--vg-safe-area-top))] pointer-events-none lg:h-[126px]"
            : "h-[calc(92px+var(--vg-safe-area-top))] pointer-events-none lg:h-[92px]"
        }`}
        aria-hidden="true"
      />
      <div className="pointer-events-none relative mx-auto h-[calc(126px+var(--vg-safe-area-top))] w-full max-w-[var(--vg-mobile-shell-max)] lg:h-[126px] lg:max-w-none">
        {isDetailView ? (
          <div className="pointer-events-auto absolute inset-x-0 top-[calc(10px+var(--vg-safe-area-top))] mx-auto grid h-[51px] w-full max-w-[var(--vg-mobile-shell-max)] grid-cols-[51px_minmax(0,1fr)_51px] items-center gap-4 lg:top-[10px] lg:max-w-none lg:px-5">
            <button
              type="button"
              onClick={onPrimaryActionClick}
              aria-label={primaryLabel}
              className="flex h-[51px] w-[51px] items-center justify-center rounded-full bg-white/[0.12] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_18px_rgba(7,16,44,0.18)] backdrop-blur-[16px] transition active:scale-[0.98]"
            >
              <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px]" />
            </button>

            <h2 className="truncate text-center text-[32px] font-semibold leading-[35px] text-white">
              {detailTitle}
            </h2>

            <div aria-hidden="true" />
          </div>
        ) : (
        <div className="vg-events-mode-header-controls pointer-events-auto absolute inset-x-0 top-[calc(10px+var(--vg-safe-area-top))] flex items-center justify-between lg:top-[10px] lg:px-5">
          {isHostMode ? (
            <button
              type="button"
              onClick={onBackToEvents}
              aria-label={t("catalog_sections.back_to_events")}
              className="vg-glass-control flex h-[50px] w-[51px] shrink-0 items-center justify-center rounded-full text-white backdrop-blur-[10px] transition active:scale-[0.98]"
            >
              <img src="/assets/landing/back_arrow.svg" alt="" className="h-6 w-6" />
            </button>
          ) : (
            <HeaderIconButton
              iconSrc={primaryIconSrc}
              label={primaryLabel}
              onClick={onPrimaryActionClick}
              badgeCount={primaryBadgeCount}
            />
          )}
          <SegmentedModeToggle
            value={toggleValue}
            options={toggleOptions}
            onChange={isGuest ? onCatalogChange : isHostMode ? onHostSectionChange : onFeedSectionChange}
            ariaLabel={isGuest ? "Public catalog" : isHostMode ? t("catalog_sections.host_nav") : t("catalog_sections.feed_nav")}
            className={`vg-events-mode-toggle vg-glass-control h-[50px] !border-transparent ${
              isGuest ? "w-[199px] vg-events-mode-toggle--guest" : memberToggleLayout.className
            }`}
            buttonClassName="vg-events-mode-toggle-label"
            knobClassName="h-[40px] !rounded-[38px]"
            knobWidthByValue={{
              [firstToggleValue]: "var(--vg-events-first-width)",
              [secondToggleValue]: "var(--vg-events-second-width)",
            }}
            knobLeftByValue={{
              [firstToggleValue]: "var(--vg-events-first-left)",
              [secondToggleValue]: "var(--vg-events-second-left)",
            }}
            renderAccessory={(option) => {
              const count = option.value === "panel"
                ? Number(hostAttentionCount) || 0
                : option.value === "tickets" ? Number(ticketsAttentionCount) || 0 : 0;
              if (count <= 0) return null;
              const label = count > 99 ? "99+" : String(count);
              return (
                <span
                  className="ml-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-[5px] text-[10px] font-extrabold leading-none text-[#0A64FF] shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                  aria-label={`${count} unread ${option.value === "tickets" ? "ticket payments" : "host event updates"}`}
                >
                  {label}
                </span>
              );
            }}
            labelBoundsByValue={{
              [firstToggleValue]: { left: "var(--vg-events-first-left)", width: "var(--vg-events-first-width)" },
              [secondToggleValue]: { left: "var(--vg-events-second-left)", width: "var(--vg-events-second-width)" },
            }}
            surfaceStyle={{
              backgroundImage:
                "linear-gradient(180deg, rgba(152,180,246,0.18) 0%, rgba(76,108,190,0.24) 100%), linear-gradient(135deg, rgba(226,238,255,0.48) 0%, rgba(125,158,235,0.18) 48%, rgba(214,229,255,0.34) 100%)",
              backgroundOrigin: "border-box",
              backgroundClip: "padding-box, border-box",
            }}
            activeStyle={{
              backgroundImage:
                "linear-gradient(180deg, rgba(56,82,151,0.98) 0%, rgba(43,62,119,0.98) 100%)",
              backgroundColor: "#304783",
              borderColor: "rgba(255,255,255,0.44)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
            }}
          />
          {!isGuest && <HeaderIconButton
            iconSrc="/assets/events/map_icon.svg"
            label="Map"
            onClick={onMapClick}
            disabled={mapDisabled}
          />}
          {isGuest && <span className="h-[50px] w-[51px] shrink-0" aria-hidden="true" />}
        </div>
        )}
        {showCategoryControls && categoriesOpen ? (
          <motion.div
            className="pointer-events-auto absolute inset-x-0 top-[calc(80px+var(--vg-safe-area-top))] w-full lg:top-[80px]"
            initial={categoryHeaderMountedRef.current && !reduceMotion ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={reduceMotion ? { duration: 0 } : { delay: 0.22, duration: 0.14 }}
          >
            <HeaderCategoryPanel
              selected={selectedCategories}
              onChange={onCategoriesChange}
              onAddSubscription={onAddSubscription}
              isSubscriptionActive={isSubscriptionActive}
              userSubscriptions={userSubscriptions}
            />
          </motion.div>
        ) : null}
        {showCategoryControls && !categoriesOpen ? (
          <button
            type="button"
            onPointerDown={handleCategorySwipeStart}
            onPointerUp={handleCategorySwipeEnd}
            onPointerCancel={handleCategorySwipeCancel}
            onClick={handleCategoryToggleClick}
            aria-expanded={categoriesOpen}
            aria-label="Toggle event categories"
            className="pointer-events-auto absolute left-1/2 top-[calc(60px+var(--vg-safe-area-top))] flex h-[36px] w-[92px] -translate-x-1/2 items-center justify-center text-white/35 [touch-action:none] lg:top-[60px]"
          >
            <svg
              viewBox="0 0 70 20"
              fill="none"
              className="h-[20px] w-[70px]"
              aria-hidden="true"
            >
              <path
                d="M18 8L35 13L52 8"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
};

const normalizeCity = (raw) => {
  if (!raw) return "";
  const city = String(raw).split(",")[0].trim();
  const key = city.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (/\b(kraj|okres|region|district|county|province|oblast)$/.test(key)) return "";
  if (key === "hlavni mesto praha" || key === "praha") return "Prague";
  if (/^kosice(?:\s+(?:i|ii|iii|iv)|\s*-\s*stare mesto)$/.test(key)) return "Košice";
  if (/^bratislava\s*-\s*(petrzalka|stare mesto|ruzinov)$/.test(key)) return "Bratislava";
  return city;
};

const EMPTY_EVENT_STATE_TABS = ["for_you", "all", "joined", "liked"];

function EmptyEventState({ viewType, t, onAction, isConceptFeed = false }) {
  const tab = EMPTY_EVENT_STATE_TABS.includes(viewType) ? viewType : "for_you";
  const isLiked = tab === "liked";
  const showHint = tab !== "liked";
  const copyKey = isConceptFeed && isLiked ? "concept_liked" : tab;

  return (
    <motion.div
      className="mx-auto flex w-full max-w-[430px] flex-col items-center px-1 pb-[calc(var(--bottom-nav-clearance,112px)+18px)] pt-7 text-center lg:pb-10"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="relative flex h-[75px] w-full max-w-[360px] overflow-hidden rounded-[16px] bg-[linear-gradient(180deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.03)_100%)] text-left shadow-[0_0_0_1px_rgba(255,255,255,0.50)]"
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          className="pointer-events-none absolute -left-[38%] top-0 h-full w-[34%] bg-[linear-gradient(100deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.2)_52%,rgba(255,255,255,0)_100%)]"
          animate={{ x: ["0%", "420%"] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex h-[75px] w-full min-w-0 items-start">
          <div className="relative mr-[12px] block h-[75px] w-[75px] shrink-0 overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,rgba(255,114,154,0.9),rgba(90,190,255,0.82)_52%,rgba(124,255,211,0.72))] shadow-[0_0_0_1px_rgba(255,255,255,0.78),inset_0_1px_0_rgba(255,255,255,0.35)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_28%,rgba(255,255,255,0.42),rgba(255,255,255,0)_42%)]" />
            <div className="absolute bottom-3 left-3 right-3 h-[8px] rounded-full bg-white/24" />
            <div className="absolute bottom-6 left-4 h-[8px] w-[42px] rounded-full bg-white/30" />
          </div>
          <div className="flex h-[75px] min-w-0 flex-1 flex-col justify-between py-[8px] pr-[10px]">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="translate-y-[2px] truncate text-[18px] font-semibold leading-[22px] text-white">
                  {t(`list.empty_states.${copyKey}.card_title`)}
                </p>
              </div>
              <span
                className={`relative ml-1 h-5 w-5 shrink-0 translate-y-[-2px] ${isLiked ? "vg-empty-liked-heart" : ""}`}
                aria-hidden="true"
              >
                {isLiked ? (
                  <span className="vg-empty-liked-heart-pulse" />
                ) : null}
                <img
                  src="/assets/events/like_icon_off.svg"
                  alt=""
                  className={`absolute inset-0 h-5 w-5 ${isLiked ? "vg-empty-liked-heart-off" : ""}`}
                />
                <img
                  src="/assets/events/like_icon_on.svg"
                  alt=""
                  className={`absolute inset-0 h-5 w-5 ${isLiked ? "vg-empty-liked-heart-on" : "opacity-0"}`}
                />
              </span>
            </div>
            <div className="flex min-w-0 items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <img src="/assets/events/offline_icon.svg" alt="" className="h-[22px] w-[22px] opacity-90" />
                <img src="/assets/events/personal_icon.svg" alt="" className="h-[22px] w-[22px] opacity-90" />
                <img src="/assets/events/payable_icon.svg" alt="" className="h-[22px] w-[22px] opacity-90" />
              </div>
              <span className="shrink-0 translate-y-[5px] text-[14px] font-semibold leading-[18px] text-white/50">
                {t(`list.empty_states.${copyKey}.card_meta`)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="mt-5 max-w-[360px]">
        <p className="text-[22px] font-extrabold leading-[26px] text-white">
          {t(`list.empty_states.${copyKey}.title`)}
        </p>
        <p className="mt-2 text-[14px] font-semibold leading-[20px] text-white/68">
          {t(`list.empty_states.${copyKey}.body`)}
        </p>
        {showHint ? (
          <p className="mt-3 text-[12px] font-semibold leading-[17px] text-white/48">
            {t(`list.empty_states.${copyKey}.hint`)}
          </p>
        ) : null}
        <button
          type="button"
          onClick={onAction}
          className="mt-5 inline-flex h-[44px] items-center justify-center rounded-full border border-white/35 bg-white/12 px-6 text-[14px] font-extrabold text-white shadow-[0_10px_24px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.18)] transition hover:bg-white/18 active:scale-[0.98]"
        >
          {t(`list.empty_states.${copyKey}.action`)}
        </button>
      </div>
    </motion.div>
  );
}

const resolveRuCity = (raw) => {
  return normalizeSelectedEventCity("RU", raw) || "Yekaterinburg";
};

/**
 * ListSearch – unified catalog of events for all 4 participant tabs (for_you | all | joined | liked).
 */
const ListSearch = ({ isGuest = false, userId = null, authLoading = false }) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const { openProfileCard } = useProfileCardModal();
  const openConceptAuthor = useCallback((event, authorType) => {
    if (authorType === "club") {
      const club = event?.club;
      const path = club?.public_path || buildClubPath(club?.id || event?.club_id, club?.name || "");
      if (path === "/clubs") return;
      navigate(path, { state: { returnPath: `${location.pathname}${location.search}` } });
      return;
    }
    if (event?.concept_author?.code) openProfileCard(event.concept_author);
  }, [location.pathname, location.search, navigate, openProfileCard]);
  const { setCatalogReady: setCityLaunchCatalogReady } = useContext(CityLaunchContext);
  const { t, i18n } = useTranslation("events");
  const {
    actions: requiredActions,
    dismissForSession: dismissRequiredActionForSession,
  } = useContext(RequiredActionsContext);
  const waitlistUnreadCtx = useWaitlistUnread();
  const waitlistUnreadTotal = isGuest
    ? 0
    : (Number(waitlistUnreadCtx?.totalUnread || 0) || 0);
  const participantRequestUnreadCtx = useParticipantRequestUnread();
  const participantRequestUnreadTotal = isGuest
    ? 0
    : (Number(participantRequestUnreadCtx?.totalUnread || 0) || 0);
  const hostFeedbackUnreadCtx = useHostFeedbackUnread();
  const hostFeedbackUnreadTotal = isGuest
    ? 0
    : (Number(hostFeedbackUnreadCtx?.totalUnread || 0) || 0);
  const hostAttentionTotal =
    waitlistUnreadTotal + participantRequestUnreadTotal + hostFeedbackUnreadTotal;
  const participantRatingUnreadCtx = useParticipantRatingUnread();
  const participantRatingUnread = isGuest
    ? 0
    : (Number(participantRatingUnreadCtx?.unreadCount || 0) || 0);
  const hostAttentionByEventId = useMemo(() => {
    const waitlistUnreadByEventId = waitlistUnreadCtx?.byEventId || {};
    const participantRequestUnreadByEventId = participantRequestUnreadCtx?.byEventId || {};
    const hostFeedbackUnreadByEventId = hostFeedbackUnreadCtx?.byEventId || {};
    const merged = { ...waitlistUnreadByEventId };
    Object.entries(participantRequestUnreadByEventId).forEach(([eventId, count]) => {
      merged[eventId] = (Number(merged[eventId] || 0) || 0) + (Number(count || 0) || 0);
    });
    Object.entries(hostFeedbackUnreadByEventId).forEach(([eventId, count]) => {
      merged[eventId] = (Number(merged[eventId] || 0) || 0) + (Number(count || 0) || 0);
    });
    return merged;
  }, [
    hostFeedbackUnreadCtx?.byEventId,
    participantRequestUnreadCtx?.byEventId,
    waitlistUnreadCtx?.byEventId,
  ]);

  // ────────── Confirm-attendance modal (via ?confirm flag) ────────── //
  const [confirmEventId, setConfirmEventId] = useState(null);
  const [forceOpenConfirmModal, setForceOpenConfirmModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventParam = params.get("event");
    const shouldConfirm = params.has("confirm");

    // Гостю нельзя подтверждать посещение — отправляем на /auth
    if (shouldConfirm && isGuest) {
      navigate(buildAuthRequiredPath(), { replace: true });
      return;
    }

    if (shouldConfirm && eventParam) {
      const id = parseInt(eventParam, 10);
      if (!isNaN(id)) {
        setConfirmEventId(id);
        setForceOpenConfirmModal(true);
      }
    } else {
      setConfirmEventId(null);
      setForceOpenConfirmModal(false);
    }
  }, [location.search, isGuest, navigate]);

  const handleCloseConfirm = () => {
    setConfirmEventId(null);
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  };

  // ────────── Create-event banner click ────────── //
  const handleStartCreation = async (hostCreatedCount) => {
    if (isGuest) {
      navigate(buildAuthRequiredPath());
      return;
    }

    pendingHostCreatedCountRef.current = Number.isFinite(hostCreatedCount)
      ? hostCreatedCount
      : null;

    // Check if user has a proper profile photo before creating an event
    const ok = await checkProfilePhotoForCreate();
    if (!ok) {
      // PhotoRequestModal will be opened by checkProfilePhotoForCreate; creation will be continued from the modal callbacks
      setCreateAfterPhoto("event");
      return;
    }

    proceedToCreateEvent(pendingHostCreatedCountRef.current);
  };

  const handleCreateEventClick = () => {
    if (isGuest) {
      navigate(buildAuthRequiredPath());
      return;
    }
    if (feedSection === "concepts") {
      void checkProfilePhotoForCreate().then((hasPhoto) => {
        if (hasPhoto) setIsQuickConceptOpen(true);
        else setCreateAfterPhoto("concept");
      });
      return;
    }
    if (hasHostedCreation || hostAttentionTotal > 0) {
      setHostPanelInitialTab(hostAttentionTotal > 0 ? "events" : "overview");
      setIsMyTicketsView(false);
      setAudienceMode("host");
      return;
    }
    void handleStartCreation();
  };

  // ────────── Refs / misc state ────────── //
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const scrollContainerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const guestCatalogNavTimerRef = useRef(null);

  // The contour owns the fixed country; an optional city/radius narrows the feed.
  const [, setUserCity] = useState("");
  const [userCountry, setUserCountry] = useState("SK"); // код региона из профиля (SK / RU)
  const [guestCatalogVisualTab, setGuestCatalogVisualTab] = useState("events");

  const [viewType, setViewType] = useState("all"); // all | for_you | joined | liked
  const [audienceMode, setAudienceMode] = useState("participant"); // participant | host
  const [feedSection, setFeedSection] = useState("events"); // events | concepts
  const cachedOrganizerBannerMode = useMemo(() => readOrganizerBannerMode(userId), [userId]);
  const [hostedCreationStatus, setHostedCreationStatus] = useState(null);
  const hostStatusResolved = hostedCreationStatus?.userId === userId;
  const hasHostedCreation = hostStatusResolved
    ? hostedCreationStatus.hasCreated
    : cachedOrganizerBannerMode === "panel";
  const organizerBannerModePending = !isGuest && Boolean(userId)
    && !hostStatusResolved && cachedOrganizerBannerMode === null;
  const localHostCreationVersionRef = useRef(0);
  const feedSwipeStartRef = useRef(null);
  const [hostPanelInitialTab, setHostPanelInitialTab] = useState("overview");
  const [hostPanelFocusEventId, setHostPanelFocusEventId] = useState(null);
  const [hostPaymentsCount, setHostPaymentsCount] = useState(0);
  const [hostFeedbackEventId, setHostFeedbackEventId] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_EVENT_COUNTRY);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState(null);
  const [selectedRadiusKm, setSelectedRadiusKm] = useState(
    DEFAULT_EVENT_DISCOVERY_RADIUS_KM
  );
  const [profileReady, setProfileReady] = useState(false);
  const feedLocationKey = useMemo(
    () => JSON.stringify([selectedCountry, selectedCity, selectedMunicipalityId, selectedRadiusKm]),
    [selectedCountry, selectedCity, selectedMunicipalityId, selectedRadiusKm]
  );
  const [resolvedFeedLocationKey, setResolvedFeedLocationKey] = useState(null);
  const currentFeedLocationKeyRef = useRef(feedLocationKey);
  currentFeedLocationKeyRef.current = feedLocationKey;
  const manuallySelectedFeedLocationRef = useRef(null);
  const requestedFeedSectionRef = useRef(null);
  const isFeedLocationResolved = isGuest || resolvedFeedLocationKey === feedLocationKey;
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [selectedDiscoveryFilters, setSelectedDiscoveryFilters] = useState({
    eventKind: "",
    audienceGender: "",
    eventLanguage: "",
    participantAgeMin: "",
    participantAgeMax: "",
    socialFit: "",
  });
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(true);
  const [isParticipantRatingView, setIsParticipantRatingView] = useState(false);
  const [participantRatingDetail, setParticipantRatingDetail] = useState({
    screen: null,
    title: "",
  });
  const [isMyTicketsView, setIsMyTicketsView] = useState(false);
  const [events, setEvents] = useState([]);
  const [professionalMeetups, setProfessionalMeetups] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [catalogHasLoaded, setCatalogHasLoaded] = useState(false);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const favouriteIdsRef = useRef(favouriteIds);
  const favouriteRequestsRef = useRef(new Map());
  useEffect(() => {
    favouriteIdsRef.current = favouriteIds;
  }, [favouriteIds]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/events/professional-meetup-invitations/", {
          authOptional: true,
          suppressUnauthorizedRedirect: true,
        });
        if (active) {
          setProfessionalMeetups(
            (Array.isArray(data?.results) ? data.results : [])
              .map((invitation) => invitation.event)
              .filter(Boolean)
          );
        }
      } catch {
        if (active) setProfessionalMeetups([]);
      }
    };
    void refresh();
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openParticipantsOnMount, setOpenParticipantsOnMount] = useState(false);
  const [joinFlowEventId, setJoinFlowEventId] = useState(null);
  const [forceOpenJoinFlow, setForceOpenJoinFlow] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isEventDetailsExpanded, setIsEventDetailsExpanded] = useState(false);
  const [isEventGeoFilterOpen, setIsEventGeoFilterOpen] = useState(false);
  const [isEventDateFilterOpen, setIsEventDateFilterOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [descLoading, setDescLoading] = useState(false);
  const [modalHeight, setModalHeight] = useState(0);
  const [geoCount, setGeoCount] = useState(null);
  const [dateCount, setDateCount] = useState(null);
  const [eventCache, setEventCache] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventDetailsAnalyticsContext, setEventDetailsAnalyticsContext] = useState(null);

  useEffect(() => {
    return acquireBodyClass("events-workspace-wide");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleParticipantRatingScreen = (event) => {
      const detail = event?.detail || {};
      setParticipantRatingDetail({
        screen: detail.screen || null,
        title: detail.title || "",
      });
    };

    window.addEventListener("vg-participant-rating-screen", handleParticipantRatingScreen);
    return () => {
      window.removeEventListener("vg-participant-rating-screen", handleParticipantRatingScreen);
    };
  }, []);

  useEffect(() => {
    if (isParticipantRatingView) return;
    setParticipantRatingDetail({ screen: null, title: "" });
  }, [isParticipantRatingView]);

  // ────────── Photo request state for create-event ────────── //
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [hasCustomAvatar, setHasCustomAvatar] = useState(null); // null = unknown, true/false = checked
  const [createAfterPhoto, setCreateAfterPhoto] = useState(false);
  const [isQuickConceptOpen, setIsQuickConceptOpen] = useState(false);
  const pendingHostCreatedCountRef = useRef(null);
  const handleCreateEventClickRef = useRef(handleCreateEventClick);
  handleCreateEventClickRef.current = handleCreateEventClick;

  useEffect(() => {
    const handleBottomCreateAction = (event) => {
      if (isGuest || audienceMode === "host" || feedSection !== "concepts") return;
      event.preventDefault();
      handleCreateEventClickRef.current();
    };
    window.addEventListener("vg:events-create-action", handleBottomCreateAction);
    return () => window.removeEventListener("vg:events-create-action", handleBottomCreateAction);
  }, [audienceMode, feedSection, isGuest]);

  useEffect(() => () => {
    if (guestCatalogNavTimerRef.current) {
      clearTimeout(guestCatalogNavTimerRef.current);
    }
  }, []);

  // ────────── Photo-check helpers for create-event ────────── //
  const checkProfilePhotoForCreate = async () => {
    // If we already know the user has a custom avatar, allow create immediately
    if (hasCustomAvatar === true) return true;

    // If we know there is no avatar and photo is required, open the modal
    if (hasCustomAvatar === false) {
      setIsPhotoModalOpen(true);
      return false;
    }

    // First-time check: fetch profile info
    try {
      const { data } = await api.get("/accounts/profile/", {
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      });

      // Strong backend flag: if photo is not required now, do not block creation
      const photoRequiredNow =
        typeof data?.photo_required_now === "boolean"
          ? data.photo_required_now
          : false;

      if (!photoRequiredNow) {
        // No need to force photo right now – treat as OK
        setHasCustomAvatar(true);
        return true;
      }

      const url = data?.avatar_custom_url || null;

      if (url) {
        // Custom avatar is already set – all good
        setHasCustomAvatar(true);
        return true;
      }

      // Photo is required now and there is no custom avatar – show modal
      setHasCustomAvatar(false);
      setIsPhotoModalOpen(true);
      return false;
    } catch (err) {
      console.error("[ListSearch] profile check failed", err);
      // On error, do not block creation; allow the flow to continue
      return true;
    }
  };

  const proceedToCreateEvent = (hostCreatedCount = null) => {
    navigate("/events/create", {
      state: {
        hostCreatedCount,
        autoOpenOrganizerLimitModal: true,
        organizerEntryIntent: "create_event",
      },
    });
  };

  // ────────── Helpers ────────── //
  const safelyAbortPreviousRequest = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
  };

  /** fetchEvents – unified loader with cancellation & freshness check */
  const fetchEvents = useCallback(
    async ({ reset = false, pageToFetch = 1, showLoader = true }) => {
      safelyAbortPreviousRequest();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const currentRequestId = ++requestIdRef.current;

      if (reset) {
        setEvents([]);
        setPage(pageToFetch);
      }
      if (showLoader) setIsLoading(true);
      if (reset && scrollContainerRef.current) {
        try {
          scrollContainerRef.current.scrollTop = 0;
        } catch (_) {}
      }

      try {
        const buildParams = (apiViewType) => ({
          city: selectedCity,
          ...(selectedCity && selectedMunicipalityId ? { municipality_id: selectedMunicipalityId } : {}),
          ...(selectedCity
            ? getEventDiscoveryLocationParams(selectedRadiusKm)
            : {}),
          country: selectedCountry,
          type: apiViewType,
          page: pageToFetch,
          page_size: PAGE_SIZE,
          ...(selectedCategories.length && { category: selectedCategories }),
          ...(selectedDate && (isGuest || feedSection === "events") && { date: selectedDate }),
          ...(!isGuest && { event_kind: feedSection === "concepts" ? "concept" : "event" }),
          ...(!isGuest && feedSection === "events" && ["all", "for_you"].includes(apiViewType)
            ? { include_team_concepts: 1 }
            : {}),
          ...(selectedDiscoveryFilters.audienceGender && { audience_gender: selectedDiscoveryFilters.audienceGender }),
          ...(selectedDiscoveryFilters.eventLanguage && { event_language: selectedDiscoveryFilters.eventLanguage }),
          ...(selectedDiscoveryFilters.participantAgeMin && { participant_age_min: selectedDiscoveryFilters.participantAgeMin, participant_age_max: selectedDiscoveryFilters.participantAgeMax }),
          ...(selectedDiscoveryFilters.socialFit && ["all", "for_you"].includes(apiViewType) && { social_fit: selectedDiscoveryFilters.socialFit }),
        });

        let data;
        try {
          const response = await api.get("/events/events_packs/", {
            params: buildParams(viewType),
            signal: controller.signal,
            authOptional: true,
            suppressUnauthorizedRedirect: true,
          });
          data = response.data;
        } catch (fetchErr) {
          const status = Number(fetchErr?.response?.status || 0);
          const canFallbackToLegacyPublicFeed =
            viewType === "all" && (status === 400 || status === 403);
          if (!canFallbackToLegacyPublicFeed) {
            throw fetchErr;
          }
          const response = await api.get("/events/events_packs/", {
            params: buildParams("for_you"),
            signal: controller.signal,
            authOptional: true,
            suppressUnauthorizedRedirect: true,
          });
          data = response.data;
        }

        if (currentRequestId !== requestIdRef.current) return; // stale

        const resultCount = Number(data?.count);
        setTotalCount(Number.isFinite(resultCount) ? resultCount : 0);
        setGeoCount(Number.isFinite(resultCount) ? resultCount : 0);
        const rawResults = Array.isArray(data?.results) ? data.results : [];
        const normalizedResults = rawResults
          .map((ev) => normalizeEventPayload(ev))
          .filter((ev) => ev && ev.is_validated)
          .map((ev) => {
            const pending = favouriteRequestsRef.current.get(String(ev.id));
            return pending ? { ...ev, is_favourite: pending.desired } : ev;
          });

        if (reset) {
          setFavouriteIds(
            new Set(
              normalizedResults
                .filter((ev) => ev.is_favourite)
                .map((ev) => ev.id)
            )
          );
          setEvents(normalizedResults);
        } else {
          setFavouriteIds((prev) => {
            const s = new Set(prev);
            normalizedResults.forEach((ev) => ev.is_favourite && s.add(ev.id));
            return s;
          });
          setEvents((prev) => {
            const byId = new Map(prev.map((e) => [e.id, e]));
            for (const e of normalizedResults) {
              if (!byId.has(e.id)) byId.set(e.id, e);
            }
            return Array.from(byId.values());
          });
        }
        setHasMore(Boolean(data?.has_next));
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("Events fetch error", err);
        }
      } finally {
        if (showLoader && currentRequestId === requestIdRef.current)
          setIsLoading(false);
      }
    },
    [selectedCity, selectedMunicipalityId, selectedCountry, selectedRadiusKm, viewType, selectedCategories, selectedDate, selectedDiscoveryFilters, feedSection, isGuest]
  );

  const prepareFilterReload = () => {
    safelyAbortPreviousRequest();
    requestIdRef.current += 1;
    setEvents([]);
    setPage(1);
    setHasMore(false);
    setLoadingMore(false);
    setIsLoading(true);
  };

  // ────────── Load more events (no manual scroll jump) ────────── //
  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;

    const next = page + 1;
    setLoadingMore(true);

    const promise = fetchEvents({
      reset: false,
      pageToFetch: next,
      showLoader: false,
    });
    setPage(next);

    if (promise && typeof promise.finally === "function") {
      promise.finally(() => setLoadingMore(false));
    } else {
      setLoadingMore(false);
    }
  };

  // ────────── IntersectionObserver for auto-load more ────────── //
  useEffect(() => {
    if (!hasMore || isLoading || loadingMore) return;
    const node = loadMoreRef.current;
    const root = scrollContainerRef.current || null;
    if (!node) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      { root, rootMargin: "400px" }
    );

    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, isLoading, loadingMore, handleLoadMore]);

  // ────────── Favourites toggle ────────── //
  const applyFavouriteState = useCallback((eventId, isFavourite) => {
    const nextIds = new Set(favouriteIdsRef.current);
    if (isFavourite) nextIds.add(eventId);
    else nextIds.delete(eventId);
    favouriteIdsRef.current = nextIds;
    setFavouriteIds(nextIds);
    setEvents((prev) => prev.map((item) =>
      String(item?.id) === String(eventId)
        ? { ...item, is_favourite: isFavourite }
        : item
    ));
    setEventCache((prev) => {
      if (!prev[eventId]) return prev;
      return {
        ...prev,
        [eventId]: { ...prev[eventId], is_favourite: isFavourite },
      };
    });
    setSelectedEvent((prev) =>
      String(prev?.id) === String(eventId)
        ? { ...prev, is_favourite: isFavourite }
        : prev
    );
  }, []);

  const toggleFavourite = (eventId) => {
    if (isGuest) {
      navigate(buildAuthRequiredPath());
      return false;
    }
    const currentlyFavourite = favouriteIdsRef.current.has(eventId);
    const nextFavourite = !currentlyFavourite;
    const key = String(eventId);
    let request = favouriteRequestsRef.current.get(key);
    if (!request) {
      request = { confirmed: currentlyFavourite, desired: nextFavourite, promise: null };
      favouriteRequestsRef.current.set(key, request);
    } else {
      request.desired = nextFavourite;
    }
    applyFavouriteState(eventId, nextFavourite);

    if (!request.promise) {
      request.promise = (async () => {
        while (request.confirmed !== request.desired) {
          const requested = request.desired;
          try {
            await api.post(`/events/${requested ? "add-to-favourites" : "remove-from-favourites"}/${eventId}/`);
            request.confirmed = requested;
          } catch (error) {
            console.error("Favourite toggle failed", error);
            if (request.desired === requested) {
              request.desired = request.confirmed;
              applyFavouriteState(eventId, request.confirmed);
            }
            break;
          }
        }
      })().finally(() => {
        favouriteRequestsRef.current.delete(key);
      });
    }
    return true;
  };

  const handleDetailsLoadError = useCallback(
    (eventOrId, err) => {
      const status = err?.response?.status;
      if (status !== 404) {
        console.error("Failed to load event details", err);
        return false;
      }

      const eventId = Number(
        typeof eventOrId === "object" ? eventOrId?.id : eventOrId
      );

      setIsDescriptionOpen(false);
      setIsEventDetailsExpanded(false);
      setModalHeight(0);
      setSelectedEvent((prev) => (prev?.id === eventId ? null : prev));
      setEventCache((prev) => {
        if (!prev[eventId]) return prev;
        const copy = { ...prev };
        delete copy[eventId];
        return copy;
      });
      setEvents((prev) => prev.filter((item) => item.id !== eventId));

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("vg-events-modal-toggle", {
            detail: { isOpen: false },
          })
        );
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t(
                "list.event_unavailable",
                "This event is no longer available."
              ),
            },
          })
        );
      }

      return true;
    },
    [t]
  );

  // ────────── Event details helpers (tap on list) ────────── //
  const handleSelect = async (ev, analyticsContext = null) => {
    // Guard against clicks on empty areas that may pass undefined
    if (!ev || !ev.id) return;

    if (
      !isDesktop &&
      selectedEvent?.id === ev.id &&
      isDescriptionOpen &&
      eventCache[ev.id]
    ) {
      setIsDescriptionOpen(false);
      setIsEventDetailsExpanded(true);
      setModalHeight(0);
      setEventDetailsAnalyticsContext((prev) => ({
        surface: analyticsContext?.surface || prev?.surface || "event_list",
        list_type: analyticsContext?.list_type || prev?.list_type || viewType,
        open_mode: "details",
      }));
      return;
    }

    const nextAnalyticsContext = {
      surface: analyticsContext?.surface || "event_list",
      list_type: analyticsContext?.list_type || viewType,
      open_mode: "description",
    };
    setEventDetailsAnalyticsContext(nextAnalyticsContext);
    trackEventEssentialAnalytics({
      eventId: ev?.id,
      action: "open",
      surface: nextAnalyticsContext.surface || "list",
    });

    setOpenParticipantsOnMount(false);
    setSelectedEvent(ev);

    // Если детали уже есть в кэше – просто открываем Short Description и шлём событие
    if (eventCache[ev.id]) {
      setIsDescriptionOpen(true);
      setIsEventDetailsExpanded(false);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("vg-events-modal-toggle", {
            detail: { isOpen: true },
          })
        );
      }

      return;
    }

    // Деталей ещё нет – грузим, кэшируем, открываем Short Description и шлём событие
    setDescLoading(true);
    try {
      const { data } = await api.get(`/events/details/${ev.id}/`, {
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      });
      setEventCache((prev) => ({ ...prev, [ev.id]: data }));
      setIsDescriptionOpen(true);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("vg-events-modal-toggle", {
            detail: { isOpen: true },
          })
        );
      }
    } catch (err) {
      handleDetailsLoadError(ev, err);
    } finally {
      setDescLoading(false);
    }
  };

  const openEventDetails = async (ev, analyticsContext = null) => {
    // Guard against accidental calls with undefined
    if (!ev || !ev.id) return;

    const nextAnalyticsContext = {
      surface: analyticsContext?.surface || "event_list",
      list_type: analyticsContext?.list_type || viewType,
      open_mode: "details",
    };
    setEventDetailsAnalyticsContext(nextAnalyticsContext);
    trackEventEssentialAnalytics({
      eventId: ev?.id,
      action: "open",
      surface: nextAnalyticsContext.surface || "list",
    });

    setOpenParticipantsOnMount(false);
    setSelectedEvent(ev);
    setIsDescriptionOpen(false);
    setModalHeight(0);
    setIsEventDetailsExpanded(false);
    setDescLoading(true);
    try {
      if (!eventCache[ev.id]) {
        const { data } = await api.get(`/events/details/${ev.id}/`, {
          authOptional: true,
          suppressUnauthorizedRedirect: true,
        });
        setEventCache((prev) => ({ ...prev, [ev.id]: data }));
      }
      setIsEventDetailsExpanded(true);
    } catch (err) {
      handleDetailsLoadError(ev, err);
    } finally {
      setDescLoading(false);
    }
  };

  // ────────── Deep-link helper (for ?event=123) ────────── //
  const openEventById = useCallback(
    async (eventId) => {
      const idNum = parseInt(eventId, 10);
      if (isNaN(idNum)) return;
      try {
        let evData = eventCache[idNum];
        if (!evData) {
          const { data } = await api.get(`/events/details/${idNum}/`, {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
          });
          setEventCache((prev) => ({ ...prev, [idNum]: data }));
          evData = data;
        }
        if (selectedEvent?.id === idNum && isEventDetailsExpanded) return;

        setEventDetailsAnalyticsContext({
          surface: "event_deep_link",
          list_type: viewType,
          open_mode: "details",
        });
        trackEventEssentialAnalytics({
          eventId: evData?.id,
          action: "open",
          surface: "event_deep_link",
        });
        setSelectedEvent(evData);
        setIsDescriptionOpen(false);
        setIsEventDetailsExpanded(true);
      } catch (err) {
        handleDetailsLoadError(idNum, err);
      }
    },
    [eventCache, handleDetailsLoadError, selectedEvent, isEventDetailsExpanded, viewType]
  );

  // ────────── Profile / subscriptions load once ────────── //
  useEffect(() => {
    // REGION: для гостей определяем регион по IP (RU/SK) через backend geo endpoint
    if (isGuest) {
      let isMounted = true;
      (async () => {
        try {
          const { data } = await api.get("/accounts/ui_language/", {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
          });
          if (!isMounted) return;

          const rawCountry = String(data?.country || "").trim().toUpperCase();
          const region = data?.registration_region === "RU" || normalizeProfileCountry(rawCountry) === "RU"
            ? "RU" : "SK";
          const country = getEventCountryValue(region, rawCountry);
          const city =
            region === "RU"
              ? resolveRuCity(data?.city)
              : normalizeCity(data?.city || "");
          const savedLocation = readEventLocationFilter(region, country);
          const savedCity = savedLocation?.city || "";
          const restoredCity = normalizeSelectedEventCity(region, savedCity);

          setUserCountry(region);
          setSelectedCountry(country);
          setUserCity(city);
          setSelectedCity(restoredCity || city);
          setSelectedMunicipalityId(normalizeMunicipalityId(
            restoredCity
              ? savedLocation?.municipalityId || (restoredCity === city ? data?.municipality_id : null)
              : data?.municipality_id
          ));
          setSelectedRadiusKm(
            savedLocation
              ? normalizeOptionalEventDiscoveryRadiusKm(savedLocation.radiusKm)
              : DEFAULT_EVENT_DISCOVERY_RADIUS_KM
          );
        } catch (err) {
          if (!isMounted) return;
          console.error("Guest geo detection failed – defaults used", err);
        } finally {
          if (isMounted) setProfileReady(true);
        }
      })();

      return () => {
        isMounted = false;
      };
    }

    (async () => {
      try {
        const { data } = await api.get("/accounts/profile/", {
          authOptional: true,
          suppressUnauthorizedRedirect: true,
        });

        // Keep the RU/EU contour separate from the profile's actual country.
        const rawCountry = (data.country || "").trim().toUpperCase();
        const region = normalizeProfileCountry(rawCountry) === "RU" ? "RU" : "SK";
        const country = getEventCountryValue(region, rawCountry);
        setUserCountry(region);

        const profileCity = normalizeCity(data.city || "");

        let city;

        if (region === "RU") {
          city = resolveRuCity(profileCity);
        } else {
          city = profileCity;
        }
        const savedLocation = readEventLocationFilter(region, country);
        const savedCity = savedLocation?.city || "";
        const restoredCity = normalizeSelectedEventCity(region, savedCity);

        setSelectedCountry(country);
        setSelectedCity(restoredCity || city);
        setSelectedMunicipalityId(normalizeMunicipalityId(
          restoredCity
            ? savedLocation?.municipalityId || (restoredCity === city ? data?.municipality_id : null)
            : data?.municipality_id
        ));
        setSelectedRadiusKm(
          savedLocation
            ? normalizeOptionalEventDiscoveryRadiusKm(savedLocation.radiusKm)
            : DEFAULT_EVENT_DISCOVERY_RADIUS_KM
        );
        setUserCity(city);
      } catch (err) {
        console.error("Profile fetch failed – defaults used", err);
        // При ошибке остаёмся в дефолтной стране текущего контура.
      } finally {
        setProfileReady(true);
      }
    })();
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) return;
    (async () => {
      try {
        const { data } = await api.get("events/user-subscriptions/");
        setUserSubscriptions(data.subscriptions || []);
      } catch (err) {
        console.error("Failed to load user subscriptions", err);
      }
    })();
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) return undefined;
    let active = true;
    const requestVersion = localHostCreationVersionRef.current;
    api.get("/events/get-host-rating/", { meta: { suppressGlobalError: true } })
      .then(({ data }) => {
        if (!active || requestVersion !== localHostCreationVersionRef.current) return;
        const hasCreated = Number(data?.host_created || 0) > 0;
        setHostedCreationStatus({ userId, hasCreated });
        writeOrganizerBannerMode(userId, hasCreated ? "panel" : "create");
      })
      .catch(() => {
        if (active && requestVersion === localHostCreationVersionRef.current) {
          setHostedCreationStatus({ userId, hasCreated: false });
        }
      });
    return () => { active = false; };
  }, [isGuest, userId]);

  // Choose the section with more visible activity for each selected location.
  // Count probes use the same location and public-feed visibility rules as the list.
  useEffect(() => {
    if (isGuest || !profileReady) return undefined;

    manuallySelectedFeedLocationRef.current = null;
    if (requestedFeedSectionRef.current) {
      setFeedSection(requestedFeedSectionRef.current);
      requestedFeedSectionRef.current = null;
      manuallySelectedFeedLocationRef.current = feedLocationKey;
      setResolvedFeedLocationKey(feedLocationKey);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    let active = true;
    const locationParams = {
      country: selectedCountry,
      city: selectedCity,
      ...(selectedCity && selectedMunicipalityId ? { municipality_id: selectedMunicipalityId } : {}),
      ...(selectedCity ? getEventDiscoveryLocationParams(selectedRadiusKm) : {}),
      type: "all",
      count_only: 1,
    };

    Promise.all(["event", "concept"].map((eventKind) =>
      api.get("/events/events_packs/", {
        params: {
          ...locationParams,
          event_kind: eventKind,
          ...(eventKind === "event" ? { include_team_concepts: 1 } : {}),
        },
        signal: controller.signal,
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
    ))
      .then(([eventResponse, conceptResponse]) => {
        if (!active || currentFeedLocationKeyRef.current !== feedLocationKey
          || manuallySelectedFeedLocationRef.current === feedLocationKey) return;
        const rawEventCount = eventResponse?.data?.count;
        const rawConceptCount = conceptResponse?.data?.count;
        const eventCount = Number(rawEventCount);
        const conceptCount = Number(rawConceptCount);
        if (rawEventCount != null && rawConceptCount != null
          && Number.isFinite(eventCount) && Number.isFinite(conceptCount)) {
          setFeedSection((current) => conceptCount > eventCount
            ? "concepts"
            : eventCount > conceptCount ? "events" : current);
        }
        setResolvedFeedLocationKey(feedLocationKey);
      })
      .catch((error) => {
        if (!active || currentFeedLocationKeyRef.current !== feedLocationKey
          || manuallySelectedFeedLocationRef.current === feedLocationKey) return;
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
          console.error("Feed section count error", error);
        }
        setResolvedFeedLocationKey(feedLocationKey);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      active = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [feedLocationKey, isGuest, profileReady, selectedCity, selectedCountry, selectedMunicipalityId, selectedRadiusKm]);

  // ────────── Main fetch trigger ────────── //
  useEffect(() => {
    if (!profileReady || !isFeedLocationResolved) return;
    fetchEvents({ reset: true, pageToFetch: 1 });
  }, [fetchEvents, isFeedLocationResolved, profileReady]);

  // ────────── Deep-link effect – open modal if ?event= present ────────── //
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventParam = params.get("event");
    const shouldOpenHostClaim = params.has("host_claim");
    const shouldOpenParticipants = params.get("participants") === "1";
    if (eventParam && !shouldOpenHostClaim) {
      setOpenParticipantsOnMount(shouldOpenParticipants);
      openEventById(eventParam).then(() => {
        const keepQueryKeys = [
          "confirm",
          "intent",
          "activation_date",
          "host_confirm",
          "force_payment",
          "pay",
          "manual_pay",
          "manual_payment",
          "confirm_intent",
          "confirm_attend",
          "concept_poll",
          "host_action",
          "host_claim",
        ];
        const shouldKeepSearch = keepQueryKeys.some((key) => params.has(key));
        if (!shouldKeepSearch) {
          navigate(
            { pathname: location.pathname, search: "" },
            { replace: true }
          );
        }
      });
    }
  }, [location.pathname, location.search, openEventById, navigate]);


  // ────────── Cleanup ────────── //
  useEffect(() => () => safelyAbortPreviousRequest(), []);

  // Dispatch custom event when ANY overlay (filters, FULL details, subscriptions) opens/closes
  useEffect(() => {
    const isAnyEventModalOpen =
      isDescriptionOpen ||
      isEventDetailsExpanded ||
      isEventGeoFilterOpen ||
      isEventDateFilterOpen ||
      isSubscriptionModalOpen;

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("vg-events-modal-toggle", {
          detail: { isOpen: isAnyEventModalOpen },
        })
      );
    }
  }, [
    isDescriptionOpen,
    isEventDetailsExpanded,
    isEventGeoFilterOpen,
    isEventDateFilterOpen,
    isSubscriptionModalOpen,
  ]);

  const cityLabel = selectedCity || selectedCountry || DEFAULT_EVENT_COUNTRY;
  const feedEvents = useMemo(() => {
    if (viewType !== "for_you") return events;
    return events.filter((ev) => {
      if (ev?.social_preference_fit?.is_recommended === false) return false;
      const fitStatus = String(ev?.social_preference_fit?.status || "").trim().toLowerCase();
      return fitStatus !== "outside" || ev?.social_preference_fit?.is_within_audience === true;
    });
  }, [events, viewType]);
  const visibleProfessionalMeetups = useMemo(() => {
    if (feedSection === "concepts") return [];
    if (["all", "for_you"].includes(viewType)) return professionalMeetups;
    if (viewType === "joined") return professionalMeetups.filter((event) => event.is_participant);
    return [];
  }, [professionalMeetups, viewType, feedSection]);
  const attendanceBoundaryNowMs = useEventAttendanceBoundaryClock(
    events
      .filter((ev) => Boolean(ev?.is_participant))
      .map((ev) => ev?.date_time)
  );
  const highlightedCurrentEvents = useMemo(() => {
    if (!["all", "for_you", "joined"].includes(viewType)) return [];

    const now = attendanceBoundaryNowMs;
    return events.filter((ev) => {
      if (!Boolean(ev?.is_participant)) return false;
      const status = String(ev?.status || "").toLowerCase();
      if (status === "pending") return true;
      const ts = new Date(ev?.date_time).getTime();
      if (!Number.isFinite(ts)) return false;
      return ts >= now && ts <= now + EVENT_ATTENDANCE_OPENS_BEFORE_MS;
    });
  }, [attendanceBoundaryNowMs, events, viewType]);
  const handleConfirmAttendanceFromCurrent = useCallback(
    (ev) => {
      if (!ev?.id) return;
      if (isGuest) {
        navigate(buildAuthRequiredPath());
        return;
      }
      navigate(
        {
          pathname: location.pathname,
          search: `?event=${ev.id}&confirm=1`,
        },
        { replace: false }
      );
    },
    [isGuest, navigate, location.pathname]
  );

  const handleJoinSessionFromCurrent = useCallback(
    (ev) => {
      if (!ev?.id) return;
      const action = requiredActions.find(
        (item) =>
          item?.type === "join_party_session"
          && Number(item?.event?.id) === Number(ev.id)
      );
      if (action?.id) {
        dismissRequiredActionForSession?.();
      }
      navigate(`/events/${ev.id}/party-session`);
    },
    [dismissRequiredActionForSession, navigate, requiredActions]
  );

  const handlePreviewShowMap = useCallback(
    (ev) => {
      if (isGuest) return;
      if (!ev?.id) return;
      navigate("/events/map_search", { state: buildEventMapState(ev) });
    },
    [isGuest, navigate]
  );

  const closeEventOverlays = useCallback(() => {
    setSelectedEvent(null);
    setIsDescriptionOpen(false);
    setIsEventDetailsExpanded(false);
    setOpenParticipantsOnMount(false);
    setForceOpenConfirmModal(false);
    setJoinFlowEventId(null);
    setForceOpenJoinFlow(false);
    setModalHeight(0);
    setEventDetailsAnalyticsContext(null);
  }, []);

  const closeCategoryPanel = useCallback(() => {
    setIsCategoryPanelOpen(false);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawEventId = params.get("host_comments_event");
    if (!rawEventId) {
      setHostFeedbackEventId(null);
      return;
    }
    if (isGuest) {
      navigate(buildAuthRequiredPath(), { replace: true });
      return;
    }

    const eventId = Number(rawEventId);
    if (!Number.isInteger(eventId) || eventId <= 0) return;
    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);
    setIsParticipantRatingView(false);
    setIsMyTicketsView(false);
    setHostPanelInitialTab("events");
    setHostFeedbackEventId(eventId);
    setAudienceMode("host");
  }, [closeEventOverlays, isGuest, location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("host_claim")) return;
    closeEventOverlays();
  }, [closeEventOverlays, location.search]);

  useEffect(() => {
    const requestedMode = location.state?.audienceMode;
    const requestedFeedSection = location.state?.feedSection;
    const hasRequestedMode = requestedMode === "host" || requestedMode === "participant";
    const hasRequestedFeedSection = requestedFeedSection === "events" || requestedFeedSection === "concepts";
    if (!hasRequestedMode && !hasRequestedFeedSection) return;
    const requestedHostPanelTab = ["overview", "events", "analytics", "payments"].includes(
      location.state?.hostPanelTab
    )
      ? location.state.hostPanelTab
      : "overview";
    const requestedHostPanelFocusEventId = Number(location.state?.hostPanelFocusEventId);

    if (isGuest && requestedMode === "host") {
      navigate(buildAuthRequiredPath(), { replace: true });
      return;
    }

    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);
    setIsParticipantRatingView(false);
    setIsMyTicketsView(requestedMode === "host" && requestedHostPanelTab === "payments");
    setHostFeedbackEventId(null);
    setHostPanelInitialTab(requestedHostPanelTab === "payments" ? "overview" : requestedHostPanelTab);
    setHostPanelFocusEventId(
      Number.isInteger(requestedHostPanelFocusEventId) && requestedHostPanelFocusEventId > 0
        ? requestedHostPanelFocusEventId
        : null
    );
    if (hasRequestedMode) setAudienceMode(requestedMode);
    if (hasRequestedFeedSection && !isGuest) {
      setFeedSection(requestedFeedSection);
      if (profileReady) {
        manuallySelectedFeedLocationRef.current = feedLocationKey;
        setResolvedFeedLocationKey(feedLocationKey);
      } else {
        requestedFeedSectionRef.current = requestedFeedSection;
      }
    }

    const nextState = { ...(location.state || {}) };
    delete nextState.audienceMode;
    delete nextState.hostPanelTab;
    delete nextState.hostPanelFocusEventId;
    delete nextState.feedSection;
    navigate(
      { pathname: location.pathname, search: location.search },
      {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      }
    );
  }, [
    closeEventOverlays,
    isGuest,
    location.pathname,
    location.search,
    location.state,
    navigate,
    profileReady,
    feedLocationKey,
  ]);

  const handleBackToEvents = useCallback(() => {
    closeEventOverlays();
    setIsMyTicketsView(false);
    setAudienceMode("participant");
  }, [closeEventOverlays]);

  const handleFeedSectionChange = useCallback((nextSection) => {
    if (!["events", "concepts"].includes(nextSection)) return;
    if (!profileReady) requestedFeedSectionRef.current = nextSection;
    manuallySelectedFeedLocationRef.current = feedLocationKey;
    setResolvedFeedLocationKey(feedLocationKey);
    if (nextSection === feedSection) return;
    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);
    abortControllerRef.current?.abort();
    requestIdRef.current += 1;
    setEvents([]);
    setPage(1);
    setHasMore(false);
    setLoadingMore(false);
    setIsLoading(true);
    setFeedSection(nextSection);
  }, [closeEventOverlays, feedLocationKey, feedSection, profileReady]);

  const handleHostSectionChange = useCallback((nextSection) => {
    closeEventOverlays();
    if (nextSection === "panel") setIsMyTicketsView(false);
    if (nextSection === "tickets") setIsMyTicketsView(true);
  }, [closeEventOverlays]);

  const handleFeedTouchStart = useCallback((event) => {
    if (isDesktop || isGuest || event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (touch.clientX < 24 || event.target.closest('button, a, input, textarea, select, [role="slider"]')) return;
    feedSwipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, [isDesktop, isGuest]);

  const handleFeedTouchEnd = useCallback((event) => {
    const start = feedSwipeStartRef.current;
    feedSwipeStartRef.current = null;
    if (!start || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0 && feedSection === "events") handleFeedSectionChange("concepts");
    if (dx > 0 && feedSection === "concepts") handleFeedSectionChange("events");
  }, [feedSection, handleFeedSectionChange]);

  const handleGuestCatalogChange = useCallback(
    (nextTab) => {
      if (!isGuest) return;
      if (guestCatalogNavTimerRef.current) {
        clearTimeout(guestCatalogNavTimerRef.current);
        guestCatalogNavTimerRef.current = null;
      }

      setGuestCatalogVisualTab(nextTab);
      if (nextTab === "events") return;

      if (nextTab === "clubs") {
        guestCatalogNavTimerRef.current = setTimeout(() => {
          navigate("/clubs?stage=directory");
        }, PUBLIC_CATALOG_NAV_DELAY_MS);
      }
    },
    [isGuest, navigate]
  );

  const handleHeaderPrimaryActionClick = useCallback(() => {
    if (isGuest) {
      navigate("/auth?tab=register");
      return;
    }
    if (isParticipantRatingView) {
      if (participantRatingDetail.screen === "rules") {
        window.dispatchEvent(new CustomEvent("vg-participant-rating-rules-close"));
        return;
      }
      setIsParticipantRatingView(false);
      return;
    }
    if (isMyTicketsView) {
      setIsMyTicketsView(false);
      return;
    }
    if (audienceMode === "host") {
      closeEventOverlays();
      setIsEventGeoFilterOpen(false);
      setIsEventDateFilterOpen(false);
      setIsCategoryPanelOpen(false);
      setIsMyTicketsView(true);
      return;
    }
    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);
    setIsParticipantRatingView(true);
  }, [
    audienceMode,
    closeEventOverlays,
    isGuest,
    isMyTicketsView,
    isParticipantRatingView,
    navigate,
    participantRatingDetail.screen,
  ]);

  const handleMapClick = useCallback(() => {
    if (isParticipantRatingView) return;
    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);
    navigate("/events/map_search", {
      state: {
        fromEventsList: true,
        returnAudienceMode: audienceMode,
        returnHostPanelTab: isMyTicketsView ? "payments" : "overview",
      },
    });
  }, [
    audienceMode,
    closeEventOverlays,
    isParticipantRatingView,
    isMyTicketsView,
    navigate,
  ]);

  const handleEmptyStateAction = () => {
    closeEventOverlays();
    setIsEventGeoFilterOpen(false);
    setIsEventDateFilterOpen(false);
    setIsCategoryPanelOpen(false);

    if (viewType === "joined" || viewType === "liked") {
      setViewType("all");
      setEvents([]);
      setPage(1);
      return;
    }

    setSelectedCategories([]);
    setSelectedDate(null);
    setSelectedCountry((current) => current || getEventCountryValue(userCountry));
    setSelectedCity("");
    setSelectedMunicipalityId(null);
    setSelectedRadiusKm(DEFAULT_EVENT_DISCOVERY_RADIUS_KM);
    writeEventLocationFilter(userCountry, "", DEFAULT_EVENT_DISCOVERY_RADIUS_KM, selectedCountry);
    setEvents([]);
    setPage(1);
  };

  const isSkLocale = String(i18n.language || "").toLowerCase().startsWith("sk");
  const isCsLocale = String(i18n.language || "").toLowerCase().startsWith("cs");
  const pageLang = isSkLocale ? "sk" : isCsLocale ? "cs" : "en";
  const ogImageUrl = "https://vaultgrid.net/android-chrome-512x512.png";
  const seoTitle = isSkLocale
    ? `Podujatia v ${cityLabel} | socialna platforma VaultGrid`
    : `Events in ${cityLabel} | VaultGrid social platform`;
  const seoDescription = isSkLocale
    ? `Objavte aktivne podujatia v ${cityLabel}, pridajte sa k aktivitám a spoznajte novych ludi cez socialnu platformu VaultGrid.`
    : `Browse active events in ${cityLabel}, join activities, and meet people on VaultGrid social platform.`;
  const seoHead = (
    <Helmet>
      <html lang={pageLang} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="robots" content={ROBOTS_INDEX_NOIMAGE} />
      <link rel="canonical" href="https://vaultgrid.net/events/search" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content="https://vaultgrid.net/events/search" />
      <meta property="og:image" content={ogImageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );
  const isCategoryPanelVisible =
    audienceMode === "participant" && !isParticipantRatingView && isCategoryPanelOpen;
  const categoryFeedTopClass = isCategoryPanelVisible
    ? "pt-[calc(138px+var(--vg-safe-area-top))] lg:pt-[138px]"
    : "pt-[calc(104px+var(--vg-safe-area-top))] lg:pt-[104px]";
  const participantRatingHeaderTitle = getParticipantRatingHeaderTitle(i18n.language);
  const soldTicketsHeaderTitle = getSoldTicketsHeaderTitle(i18n.language);
  const isDetailHeaderView =
    audienceMode === "participant" && isParticipantRatingView;
  const detailHeaderTitle =
    audienceMode === "host" && isMyTicketsView
      ? soldTicketsHeaderTitle
      : participantRatingDetail.title || participantRatingHeaderTitle;

  useEffect(() => {
    if (profileReady && !isLoading) setCatalogHasLoaded(true);
  }, [profileReady, isLoading]);

  const cityLaunchCatalogReady = !isGuest && profileReady && catalogHasLoaded
    && location.pathname === "/events/search" && !location.search
    && audienceMode === "participant" && !isParticipantRatingView
    && !selectedEvent && !isDescriptionOpen && !isEventDetailsExpanded
    && !isEventGeoFilterOpen && !isEventDateFilterOpen && !isSubscriptionModalOpen
    && !isPhotoModalOpen && !forceOpenConfirmModal && !forceOpenJoinFlow;

  useEffect(() => {
    setCityLaunchCatalogReady(cityLaunchCatalogReady);
    return () => setCityLaunchCatalogReady(false);
  }, [cityLaunchCatalogReady, setCityLaunchCatalogReady]);

  // ────────── UI ────────── //
  return (
    <main
      data-registration-destination="events"
      className="relative mx-auto flex h-dvh min-h-0 w-full flex-col overflow-hidden overflow-x-hidden px-[var(--vg-page-gutter)] pb-0 pt-0 text-white max-md:max-w-[991px] max-sm:max-w-screen-sm lg:h-full"
      style={EVENTS_PAGE_BACKGROUND_STYLE}
    >
      {seoHead}
      <EventsModeHeader
        mode={audienceMode}
        isGuest={isGuest}
        isDetailView={isDetailHeaderView}
        detailTitle={detailHeaderTitle}
        catalogValue={guestCatalogVisualTab}
        feedSection={feedSection}
        hostSection={isMyTicketsView ? "tickets" : "panel"}
        onCatalogChange={handleGuestCatalogChange}
        onFeedSectionChange={handleFeedSectionChange}
        onHostSectionChange={handleHostSectionChange}
        onBackToEvents={handleBackToEvents}
        onPrimaryActionClick={handleHeaderPrimaryActionClick}
        onMapClick={handleMapClick}
        participantRatingUnread={participantRatingUnread}
        hostAttentionCount={hostAttentionTotal}
        ticketsAttentionCount={hostPaymentsCount}
        mapDisabled={isParticipantRatingView}
        categoriesOpen={isCategoryPanelVisible}
        hideCategoryToggle={isDesktop && Boolean(selectedEvent && isEventDetailsExpanded)}
        onToggleCategories={() => {
          if (audienceMode !== "participant" || isParticipantRatingView) return;
          setIsCategoryPanelOpen((open) => !open);
        }}
        selectedCategories={selectedCategories}
        onCategoriesChange={(cats) => {
          setSelectedCategories(cats);
          setPage(1);
        }}
        onAddSubscription={() => {
          if (isGuest) {
            navigate(buildAuthRequiredPath());
            return;
          }
          setIsSubscriptionModalOpen(true);
        }}
        isSubscriptionActive={isSubscriptionModalOpen}
        userSubscriptions={userSubscriptions}
      />

      {audienceMode === "host" ? (
        <AnimatePresence mode="wait" initial={false}>
          {isMyTicketsView ? (
            <motion.div
              key="my-tickets-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={`-mx-[var(--vg-page-gutter)] flex-1 overflow-y-auto pt-[calc(104px+var(--vg-safe-area-top))] ${EVENT_FEED_SCROLLBAR_CLASS} lg:mb-8 lg:pt-[104px] ${EVENT_FEED_DESKTOP_SCROLL_CLASS} lg:pb-8`}
            >
              <div className={EVENT_FEED_DESKTOP_CONTENT_CLASS}>
                <EventCreateStep0
                  initialPanelOpen
                  hideProceedButton
                  organizerSection="tickets"
                  initialTab="payments"
                  initialFocusEventId={hostPanelFocusEventId}
                  onPaymentsCountChange={setHostPaymentsCount}
                  onClose={handleBackToEvents}
                />
              </div>
            </motion.div>
          ) : selectedEvent && isEventDetailsExpanded && isDesktop ? (
            <motion.div
              key="event-detail-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={EVENT_DETAIL_DESKTOP_VIEW_CLASS}
            >
              <div className={EVENT_DETAIL_DESKTOP_SHELL_CLASS}>
                <EventDetails
                  asPage
                  event={eventCache[selectedEvent.id]}
                  isFavourite={favouriteIds.has(selectedEvent.id)}
                  onToggleFavourite={toggleFavourite}
                  forceOpenConfirmModal={
                    forceOpenConfirmModal && selectedEvent.id === confirmEventId
                  }
                  forceOpenJoinFlow={
                    forceOpenJoinFlow && selectedEvent.id === joinFlowEventId
                  }
                  onForceJoinConsumed={() => setForceOpenJoinFlow(false)}
                  openParticipantsOnMount={openParticipantsOnMount}
                  onConfirmClose={handleCloseConfirm}
                  onClose={() => {
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                    setOpenParticipantsOnMount(false);
                    setForceOpenConfirmModal(false);
                    setJoinFlowEventId(null);
                    setForceOpenJoinFlow(false);
                    setEventDetailsAnalyticsContext(null);
                  }}
                  onEventUpdate={(updatedEvent = null) => {
                    if (updatedEvent?.id) {
                      setEventCache((prev) => ({
                        ...prev,
                        [updatedEvent.id]: {
                          ...(prev[updatedEvent.id] || {}),
                          ...updatedEvent,
                        },
                      }));
                      setEvents((prev) =>
                        prev.map((item) =>
                          Number(item?.id) === Number(updatedEvent.id)
                            ? { ...item, ...updatedEvent }
                            : item
                        )
                      );
                      setSelectedEvent((prev) =>
                        Number(prev?.id) === Number(updatedEvent.id)
                          ? { ...prev, ...updatedEvent }
                          : prev
                      );
                      return;
                    }
                    setEventCache((prev) => {
                      const copy = { ...prev };
                      delete copy[selectedEvent.id];
                      return copy;
                    });
                    setEvents((prev) =>
                      prev.filter((item) => item.id !== selectedEvent.id)
                    );
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                  }}
                  isGuest={isGuest}
                  onRequireAuth={() => navigate(buildAuthRequiredPath())}
                  analyticsContext={eventDetailsAnalyticsContext}
                />
              </div>
            </motion.div>
          ) : selectedEvent && isEventDetailsExpanded && isDesktop ? (
            <motion.div
              key="event-detail-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={EVENT_DETAIL_DESKTOP_VIEW_CLASS}
            >
              <div className={EVENT_DETAIL_DESKTOP_SHELL_CLASS}>
                <EventDetails
                  asPage
                  event={eventCache[selectedEvent.id]}
                  isFavourite={favouriteIds.has(selectedEvent.id)}
                  onToggleFavourite={toggleFavourite}
                  forceOpenConfirmModal={
                    forceOpenConfirmModal && selectedEvent.id === confirmEventId
                  }
                  forceOpenJoinFlow={
                    forceOpenJoinFlow && selectedEvent.id === joinFlowEventId
                  }
                  onForceJoinConsumed={() => setForceOpenJoinFlow(false)}
                  openParticipantsOnMount={openParticipantsOnMount}
                  onConfirmClose={handleCloseConfirm}
                  onClose={() => {
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                    setOpenParticipantsOnMount(false);
                    setForceOpenConfirmModal(false);
                    setJoinFlowEventId(null);
                    setForceOpenJoinFlow(false);
                    setEventDetailsAnalyticsContext(null);
                  }}
                  onEventUpdate={(updatedEvent = null) => {
                    if (updatedEvent?.id) {
                      setEventCache((prev) => ({
                        ...prev,
                        [updatedEvent.id]: {
                          ...(prev[updatedEvent.id] || {}),
                          ...updatedEvent,
                        },
                      }));
                      setEvents((prev) =>
                        prev.map((item) =>
                          Number(item?.id) === Number(updatedEvent.id)
                            ? { ...item, ...updatedEvent }
                            : item
                        )
                      );
                      setSelectedEvent((prev) =>
                        Number(prev?.id) === Number(updatedEvent.id)
                          ? { ...prev, ...updatedEvent }
                          : prev
                      );
                      return;
                    }
                    setEventCache((prev) => {
                      const copy = { ...prev };
                      delete copy[selectedEvent.id];
                      return copy;
                    });
                    setEvents((prev) =>
                      prev.filter((item) => item.id !== selectedEvent.id)
                    );
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                  }}
                  isGuest={isGuest}
                  onRequireAuth={(authUrl) => navigate(authUrl || buildAuthRequiredPath())}
                  analyticsContext={eventDetailsAnalyticsContext}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="host-create-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={`-mx-[var(--vg-page-gutter)] flex-1 overflow-y-auto pt-[calc(104px+var(--vg-safe-area-top))] lg:pt-[104px] ${EVENT_FEED_SCROLLBAR_CLASS} ${EVENT_FEED_DESKTOP_SCROLL_CLASS}`}
              style={{ overflowAnchor: "none" }}
            >
              <div className={EVENT_FEED_DESKTOP_CONTENT_CLASS}>
                <EventCreateStep0
                  initialPanelOpen
                  hideProceedButton
                  organizerSection="panel"
                  initialTab={hostPanelInitialTab}
                  initialFeedbackEventId={hostFeedbackEventId}
                  unreadByEventId={hostAttentionByEventId}
                  onPaymentsCountChange={setHostPaymentsCount}
                  showCreateEventBanner
                  onClose={handleBackToEvents}
                  onProceed={handleStartCreation}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          {isParticipantRatingView ? (
            <motion.div
              key="participant-rating-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={`-mx-[var(--vg-page-gutter)] flex-1 overflow-y-auto px-[var(--vg-page-gutter)] pt-[calc(104px+var(--vg-safe-area-top))] ${EVENT_FEED_SCROLLBAR_CLASS} lg:pt-[104px] ${EVENT_FEED_DESKTOP_SCROLL_CLASS} lg:pb-8`}
            >
              <div className={EVENT_FEED_DESKTOP_CONTENT_CLASS}>
                <ParticipantRatingPage
                  embedded
                  mode="rating"
                  onClose={() => setIsParticipantRatingView(false)}
                />
              </div>
            </motion.div>
          ) : selectedEvent && isEventDetailsExpanded && isDesktop ? (
            <motion.div
              key="event-detail-view"
              ref={scrollContainerRef}
              variants={PAGE_SWAP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              className={EVENT_DETAIL_DESKTOP_VIEW_CLASS}
            >
              <div className={EVENT_DETAIL_DESKTOP_SHELL_CLASS}>
                <EventDetails
                  asPage
                  event={eventCache[selectedEvent.id]}
                  isFavourite={favouriteIds.has(selectedEvent.id)}
                  onToggleFavourite={toggleFavourite}
                  forceOpenConfirmModal={
                    forceOpenConfirmModal && selectedEvent.id === confirmEventId
                  }
                  forceOpenJoinFlow={
                    forceOpenJoinFlow && selectedEvent.id === joinFlowEventId
                  }
                  onForceJoinConsumed={() => setForceOpenJoinFlow(false)}
                  openParticipantsOnMount={openParticipantsOnMount}
                  onConfirmClose={handleCloseConfirm}
                  onClose={() => {
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                    setOpenParticipantsOnMount(false);
                    setForceOpenConfirmModal(false);
                    setJoinFlowEventId(null);
                    setForceOpenJoinFlow(false);
                    setEventDetailsAnalyticsContext(null);
                  }}
                  onEventUpdate={(updatedEvent = null) => {
                    if (updatedEvent?.id) {
                      setEventCache((prev) => ({
                        ...prev,
                        [updatedEvent.id]: {
                          ...(prev[updatedEvent.id] || {}),
                          ...updatedEvent,
                        },
                      }));
                      setEvents((prev) =>
                        prev.map((item) =>
                          Number(item?.id) === Number(updatedEvent.id)
                            ? { ...item, ...updatedEvent }
                            : item
                        )
                      );
                      setSelectedEvent((prev) =>
                        Number(prev?.id) === Number(updatedEvent.id)
                          ? { ...prev, ...updatedEvent }
                          : prev
                      );
                      return;
                    }
                    setEventCache((prev) => {
                      const copy = { ...prev };
                      delete copy[selectedEvent.id];
                      return copy;
                    });
                    setEvents((prev) =>
                      prev.filter((item) => item.id !== selectedEvent.id)
                    );
                    setIsEventDetailsExpanded(false);
                    setSelectedEvent(null);
                  }}
                  isGuest={isGuest}
                  onRequireAuth={(authUrl) => navigate(authUrl || buildAuthRequiredPath())}
                  analyticsContext={eventDetailsAnalyticsContext}
                />
              </div>
            </motion.div>
          ) : (
        <motion.div
          key="event-feed-view"
          data-testid="event-feed-scroll"
          ref={scrollContainerRef}
          variants={PAGE_SWAP_VARIANTS}
          initial="initial"
          animate="animate"
          exit="exit"
          onScroll={closeCategoryPanel}
          onTouchStart={handleFeedTouchStart}
          onTouchEnd={handleFeedTouchEnd}
          onTouchCancel={() => { feedSwipeStartRef.current = null; }}
          className={`-mx-[var(--vg-page-gutter)] flex-1 overflow-y-auto px-[var(--vg-page-gutter)] transition-[padding] duration-200 ${categoryFeedTopClass} ${EVENT_FEED_SCROLLBAR_CLASS} ${EVENT_FEED_DESKTOP_SCROLL_CLASS} lg:pb-8 ${
            isGuest ? "pb-6" : "pb-[calc(var(--bottom-nav-clearance,112px)+16px)]"
          }`}
        >
          <div className={EVENT_FEED_DESKTOP_CONTENT_CLASS}>
          {!isGuest ? <CityLaunchBanner /> : null}
          {authLoading || organizerBannerModePending ? (
            <div
              aria-hidden="true"
              className="vg-create-event-banner-flow h-[60px] w-full rounded-2xl border border-white/85 shadow-[0_10px_24px_rgba(19,60,170,0.22),inset_0_1px_0_rgba(255,255,255,0.28)]"
            />
          ) : (
            <CreateEventBanner
              onClick={handleCreateEventClick}
              mode={feedSection === "concepts"
                ? "concept"
                : hasHostedCreation || hostAttentionTotal > 0 ? "panel" : "create"}
              badgeCount={hostAttentionTotal}
            />
          )}

          <div className="mb-2 hidden lg:block">
            <NavigationTabs
              activeTab={viewType}
              badgeCounts={{}}
              onTabChange={(tab) => {
                if (isGuest && !["for_you", "all"].includes(tab)) {
                  navigate(buildAuthRequiredPath());
                  return;
                }
                setViewType(tab);
                setEvents([]);
                setPage(1);
              }}
            />
          </div>

          <div className="mb-2 flex flex-col lg:hidden">
            <NavigationTabs
              activeTab={viewType}
              badgeCounts={{}}
              onTabChange={(tab) => {
                if (isGuest && !["for_you", "all"].includes(tab)) {
                  navigate(buildAuthRequiredPath());
                  return;
                }
                setViewType(tab);
                setEvents([]);
                setPage(1);
              }}
            />
          </div>

          <EventsFilters
            expandedHeader={isCategoryPanelVisible}
            isGeoOpen={isEventGeoFilterOpen}
            isFilterOpen={isEventDateFilterOpen}
            onGeoOpen={() => {
              setSelectedEvent(null);
              setIsEventDateFilterOpen(false);
              setIsEventGeoFilterOpen(true);
            }}
            onFilterOpen={() => {
              setSelectedEvent(null);
              setIsEventGeoFilterOpen(false);
              setIsEventDateFilterOpen(true);
            }}
            onGeoClose={() => setIsEventGeoFilterOpen(false)}
            onFilterClose={() => setIsEventDateFilterOpen(false)}
            selectedCountry={selectedCountry}
            selectedCity={selectedCity}
            selectedMunicipalityId={selectedMunicipalityId}
            selectedRadiusKm={selectedRadiusKm}
            geoCount={geoCount}
            setGeoCount={setGeoCount}
            marketRegion={userCountry === "RU" ? "RU" : "EU"}
            onLocationChange={(country, city, radiusKm, municipalityId) => {
              const nextCountry = country || selectedCountry || getEventCountryValue(userCountry);
              const nextCity = normalizeSelectedEventCity(userCountry, city);
              const nextMunicipalityId = nextCity ? normalizeMunicipalityId(municipalityId) : null;
              const nextRadiusKm = normalizeOptionalEventDiscoveryRadiusKm(radiusKm);
              const locationChanged =
                nextCountry !== selectedCountry ||
                nextCity !== selectedCity ||
                nextMunicipalityId !== selectedMunicipalityId ||
                nextRadiusKm !== selectedRadiusKm;

              if (locationChanged) {
                prepareFilterReload();
              }

              setSelectedCountry(nextCountry);
              setSelectedCity(nextCity);
              setSelectedMunicipalityId(nextMunicipalityId);
              setSelectedRadiusKm(nextRadiusKm);
              writeEventLocationFilter(userCountry, nextCity, nextRadiusKm, nextCountry, nextMunicipalityId);

              setUserCity(nextCity);
            }}
            selectedDate={selectedDate}
            selectedCategories={selectedCategories}
            selectedDiscoveryFilters={selectedDiscoveryFilters}
            fixedEventKind={!isGuest ? (feedSection === "concepts" ? "concept" : "event") : ""}
            includeTeamConcepts={!isGuest && feedSection === "events" && ["all", "for_you"].includes(viewType)}
            onDiscoveryFiltersChange={(filters) => {
              if (JSON.stringify(filters) !== JSON.stringify(selectedDiscoveryFilters)) {
                prepareFilterReload();
                setSelectedDiscoveryFilters(filters);
              }
            }}
            onCategoriesChange={(categories) => {
              if (JSON.stringify(categories) !== JSON.stringify(selectedCategories)) {
                prepareFilterReload();
                setSelectedCategories(categories);
              }
            }}
            viewType={viewType}
            isGuest={isGuest}
            dateCount={dateCount}
            setDateCount={setDateCount}
            onDateChange={(date) => {
              const nextDate = date || null;
              if (nextDate !== selectedDate) {
                prepareFilterReload();
              }
              setSelectedDate(nextDate);
            }}
            onApply={() => {
              setIsEventGeoFilterOpen(false);
              setIsEventDateFilterOpen(false);
            }}
          />

          <div className="pt-1 lg:-mx-1 lg:px-1">
            {isLoading ? (
              <EventFeedSkeleton />
            ) : feedEvents.length === 0 && highlightedCurrentEvents.length === 0 && visibleProfessionalMeetups.length === 0 ? (
              feedSection === "concepts" && viewType !== "liked" ? (
                <div className="mx-auto max-w-[430px] px-4 py-12 text-center text-white">
                  <h2 className="text-[22px] font-bold">{t("catalog_sections.empty_concepts")}</h2>
                  <p className="mt-2 text-sm text-white/70">{t("catalog_sections.empty_concepts_body")}</p>
                </div>
              ) : (
                <EmptyEventState
                  viewType={viewType}
                  t={t}
                  onAction={handleEmptyStateAction}
                  isConceptFeed={feedSection === "concepts"}
                />
              )
            ) : (
              <>
                <EventFeedErrorBoundary
                  resetKey={`${viewType}:${selectedCountry}:${selectedCity}:${selectedRadiusKm}:${selectedDate || ""}:${feedEvents.map((ev) => ev?.id).join(",")}`}
                  onRecover={() => fetchEvents({ reset: true, pageToFetch: 1 })}
                  title={t("list.feed_error.title", { defaultValue: "Events could not be shown" })}
                  body={t("list.feed_error.body", {
                    defaultValue: "Reload the feed to show the latest events.",
                  })}
                  actionLabel={t("list.feed_error.action", { defaultValue: "Reload events" })}
                >
                  <EventList
                    events={feedEvents}
                    professionalMeetups={visibleProfessionalMeetups}
                    onSelect={(ev) => {
                      if (!ev || !ev.id) return;
                      const analyticsContext = {
                        surface: "event_list",
                        list_type: viewType,
                      };
                      setIsEventGeoFilterOpen(false);
                      setIsEventDateFilterOpen(false);
                      if (isDesktop || (
                        feedSection === "concepts" && ev.type === "concept" &&
                        !ev.is_platform_event
                      )) {
                        openEventDetails(ev, analyticsContext);
                        return;
                      }
                      handleSelect(ev, analyticsContext);
                    }}
                    hideMapButton={isGuest}
                    favouriteIds={favouriteIds}
                    onToggleFavourite={toggleFavourite}
                    selectedEventId={selectedEvent?.id}
                    modalHeight={isDescriptionOpen ? modalHeight : 0}
                    eventStatuses={Object.fromEntries(
                      feedEvents.map((ev) => [ev.id, ev.status])
                    )}
                    showBottomMargin={false}
                    showMainEvent={viewType === "for_you"}
                    disableClientSort={viewType !== "for_you"}
                    showUnreadBadges={false}
                    unreadByEventId={hostAttentionByEventId}
                    analyticsSurface="event_list"
                    analyticsListType={viewType}
                    flattenHostedView={false}
                    enableForYouEnhancements={viewType === "for_you"}
                    separatePlatformEvents={["all", "for_you"].includes(viewType)}
                    highlightedEvents={highlightedCurrentEvents}
                    onConfirmAttendance={handleConfirmAttendanceFromCurrent}
                    onJoinSession={handleJoinSessionFromCurrent}
                    conceptPostLayout={!isGuest && feedSection === "concepts"}
                    isConceptFeed={feedSection === "concepts"}
                    showTeamConcepts={!isGuest && feedSection === "events" && ["all", "for_you"].includes(viewType)}
                    onOpenConceptAuthor={openConceptAuthor}
                  />
                </EventFeedErrorBoundary>
                {hasMore && (
                  <>
                    <div ref={loadMoreRef} style={{ height: 1 }} />
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="mt-2 mb-[calc(var(--bottom-nav-clearance,112px)+8px)] mx-auto block underline lg:mb-0"
                    >
                      {loadingMore
                        ? t("list.loading_more")
                        : t("list.show_next", {
                            count: Math.min(
                              PAGE_SIZE,
                              Math.max(totalCount - page * PAGE_SIZE, 0)
                            ),
                          })}
                    </button>
                  </>
                )}
              </>
            )}
            {!isGuest && feedSection === "events" && !isLoading && !hasMore ? (
              <button
                type="button"
                onClick={() => handleFeedSectionChange("concepts")}
                className="mx-auto mt-6 flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-white/45 bg-white/10 px-6 text-[15px] font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("catalog_sections.go_to_concepts")}
                <span aria-hidden="true">→</span>
              </button>
            ) : null}
          </div>
          </div>

          {/* Small description modal */}
          {selectedEvent && isDescriptionOpen && (
            <EventDescriptionModal
              event={eventCache[selectedEvent.id] || selectedEvent}
              isGuest={isGuest}
              description={eventCache[selectedEvent.id]?.description}
              loading={descLoading}
              onHeight={setModalHeight}
              onShowMap={isGuest ? undefined : handlePreviewShowMap}
              isConcept={String(selectedEvent?.type || "").toLowerCase() === "concept"}
              onClose={() => {
                setIsDescriptionOpen(false);
                setSelectedEvent(null);
                setModalHeight(0);
                setEventDetailsAnalyticsContext(null);
                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("vg-events-modal-toggle", {
                      detail: { isOpen: false },
                    })
                  );
                }
              }}
              onExpand={() => {
                setIsDescriptionOpen(false);
                setIsEventDetailsExpanded(true);
                setModalHeight(0);
                setEventDetailsAnalyticsContext((prev) => ({
                  surface: prev?.surface || "event_list",
                  list_type: prev?.list_type || viewType,
                  open_mode: "details",
                }));
              }}
            />
          )}
          {/* Full-screen details modal */}
          {selectedEvent && isEventDetailsExpanded && !isDesktop && (
            <EventDetails
              event={eventCache[selectedEvent.id]}
              isFavourite={favouriteIds.has(selectedEvent.id)}
              onToggleFavourite={toggleFavourite}
              forceOpenConfirmModal={
                forceOpenConfirmModal && selectedEvent.id === confirmEventId
              }
              forceOpenJoinFlow={
                forceOpenJoinFlow && selectedEvent.id === joinFlowEventId
              }
              onForceJoinConsumed={() => setForceOpenJoinFlow(false)}
              openParticipantsOnMount={openParticipantsOnMount}
              onConfirmClose={handleCloseConfirm}
              onClose={() => {
                setIsEventDetailsExpanded(false);
                setSelectedEvent(null);
                setOpenParticipantsOnMount(false);
                setForceOpenConfirmModal(false);
                setJoinFlowEventId(null);
                setForceOpenJoinFlow(false);
                setEventDetailsAnalyticsContext(null);
              }}
              onEventUpdate={(updatedEvent = null) => {
                if (updatedEvent?.id) {
                  setEventCache((prev) => ({
                    ...prev,
                    [updatedEvent.id]: {
                      ...(prev[updatedEvent.id] || {}),
                      ...updatedEvent,
                    },
                  }));
                  setEvents((prev) =>
                    prev.map((item) =>
                      Number(item?.id) === Number(updatedEvent.id)
                        ? { ...item, ...updatedEvent }
                        : item
                    )
                  );
                  setSelectedEvent((prev) =>
                    Number(prev?.id) === Number(updatedEvent.id)
                      ? { ...prev, ...updatedEvent }
                      : prev
                  );
                  return;
                }
                const eventId = selectedEvent.id;
                void api
                  .get(`/events/details/${eventId}/`)
                  .then(({ data }) => {
                    if (!data?.id) return;
                    setEventCache((prev) => ({
                      ...prev,
                      [data.id]: {
                        ...(prev[data.id] || {}),
                        ...data,
                      },
                    }));
                    setEvents((prev) =>
                      prev.map((item) =>
                        Number(item?.id) === Number(data.id)
                          ? { ...item, ...data }
                          : item
                      )
                    );
                    setSelectedEvent((prev) =>
                      Number(prev?.id) === Number(data.id)
                        ? { ...prev, ...data }
                        : prev
                    );
                  })
                  .catch((err) => {
                    handleDetailsLoadError(eventId, err);
                  });
                void fetchEvents({ reset: true, pageToFetch: 1, showLoader: false });
              }}
              isGuest={isGuest}
              onRequireAuth={(authUrl) => navigate(authUrl || buildAuthRequiredPath())}
              analyticsContext={eventDetailsAnalyticsContext}
            />
          )}
        </motion.div>
          )}
        </AnimatePresence>
      )}

      {isSubscriptionModalOpen && (
        <SubscriptionModal
          isOpen
          onClose={() => setIsSubscriptionModalOpen(false)}
          initialSubscriptions={userSubscriptions}
        />
      )}

      <PhotoRequestModal
        visible={isPhotoModalOpen}
        onClose={() => {
          setIsPhotoModalOpen(false);
          setCreateAfterPhoto(false);
        }}
        onUploaded={async () => {
          // Photo was successfully uploaded – mark and continue create-event flow if needed
          setHasCustomAvatar(true);
          setIsPhotoModalOpen(false);
          if (createAfterPhoto) {
            const nextAction = createAfterPhoto;
            setCreateAfterPhoto(false);
            if (nextAction === "concept") setIsQuickConceptOpen(true);
            else proceedToCreateEvent(pendingHostCreatedCountRef.current);
          }
        }}
      />
      <ConceptQuickComposer
        open={isQuickConceptOpen}
        onClose={() => setIsQuickConceptOpen(false)}
        selectedCity={selectedCity}
        selectedCountry={selectedCountry}
        selectedMunicipalityId={selectedMunicipalityId}
        onAdvanced={(quickConceptDraft) => {
          navigate("/events/create", {
            state: {
              skipIntro: true,
              quickConceptDraft,
              quickConceptStartStep: "place",
              returnPath: "/events/search",
              organizerEntryIntent: "create_concept",
            },
          });
        }}
        onCreated={() => {
          localHostCreationVersionRef.current += 1;
          setHostedCreationStatus({ userId, hasCreated: true });
          writeOrganizerBannerMode(userId, "panel");
          void fetchEvents({ reset: true, pageToFetch: 1 });
        }}
      />
    </main>
  );
};

export default ListSearch;
